import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, LineChart, Line, ReferenceLine, CartesianGrid, Legend } from 'recharts';
import Glossary, { GlossaryEntry } from '../components/Glossary';
import ComplianceBadges from '../components/ComplianceBadges';
import ViewModeToggle from '../components/ViewModeToggle';
// pdfjs-dist text extraction removed — sends PDF binary to backend instead

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type TabKey = 'import' | 'soa' | 'cpt' | 'perpatient' | 'summary' | 'compare' | 'forecast' | 'calcbudget';

interface SOAProcedure {
  name: string;
  visits: string[];
  window: string;
}

interface CPTMapping {
  procedure: string;
  cpt: string;
  description: string;
  rates: Record<string, number>;
}

interface SiteConfig {
  key: string;
  label: string;
  patients: number;
}

interface ProtocolConfig {
  id: string;
  trialName: string;
  protocolTitle: string;
  sponsor: string;
  phase: string;
  version: string;
  lastUpdated: string;
  totalPatients: number;
  patientLabel: string;
  visits: string[];
  procedures: SOAProcedure[];
  cptMappings: CPTMapping[];
  sites: SiteConfig[];
  siteSetup: number;
  monitoringCostPerVisit: number;
  monitoringVisitsPerCycle: number;
  overheadRate: number;
  dataManagementPerPatient: number;
  cycleOptions: number[];
  defaultCycles: number;
  getCycleMultiplier: (visit: string, numCycles: number) => number;
}

interface ParsedProtocol {
  protocol_id: string;
  trial_name: string;
  sponsor: string;
  phase: string;
  n_patients: number;
  visits: string[];
  procedures: string[];
  soa_grid: Record<string, Record<string, boolean>>;
  visit_windows: Record<string, string>;
  confidence_score: number;
  manual_review_needed: string[];
}

interface ImportedProtocol {
  id: string;
  name: string;
  sponsor: string;
  version: string;
  status: 'Active' | 'Draft' | 'Archived';
  lastUpdated: string;
  data?: ParsedProtocol;
}

// ---------------------------------------------------------------------------
// Protocol Definitions — Sym004-09
// ---------------------------------------------------------------------------
const SYM004_VISITS = [
  'Screening', 'C1/D1', 'C1/D8', 'C1/D15', 'C1/D22',
  'Even-C/D1', 'Even-C/D15', 'Odd-C/D1', 'Odd-C/D15',
  'EOT', '1M-FUP',
];

const SYM004_ALL_VISITS = [...SYM004_VISITS];

const SYM004_PROCEDURES: SOAProcedure[] = [
  { name: 'Informed Consent', visits: ['Screening'], window: 'Before any study procedure' },
  { name: 'Baseline/Eligibility', visits: ['Screening', 'C1/D1'], window: '28 days prior to C1/D1' },
  { name: 'Medication Survey', visits: SYM004_ALL_VISITS, window: 'Each visit' },
  { name: 'SAE Survey', visits: SYM004_ALL_VISITS, window: 'Each visit' },
  { name: 'DLT Evaluation', visits: ['C1/D1', 'C1/D8', 'C1/D15', 'C1/D22', 'Even-C/D1'], window: 'Cycle 1 only' },
  { name: 'Vital Signs', visits: ['Screening', 'C1/D1', 'C1/D15', 'C1/D22', 'Even-C/D1', 'Even-C/D15', 'Odd-C/D1', 'Odd-C/D15', 'EOT', '1M-FUP'], window: '\u00b130 min' },
  { name: 'ECOG PS', visits: ['Screening', 'C1/D1', 'Even-C/D1', 'Odd-C/D1', 'EOT', '1M-FUP'], window: '\u00b11 day' },
  { name: 'Physical Exam', visits: ['Screening', 'C1/D1', 'Even-C/D1', 'Odd-C/D1', 'EOT', '1M-FUP'], window: '\u00b12 days' },
  { name: 'Dermatological Exam', visits: ['Screening', 'C1/D1', 'C1/D15', 'Even-C/D1', 'Even-C/D15', 'Odd-C/D1', 'Odd-C/D15', 'EOT', '1M-FUP'], window: '\u00b12 days' },
  { name: 'ECG 12-lead', visits: ['Screening', 'EOT'], window: '\u00b11 day' },
  { name: 'Safety Blood Panel', visits: SYM004_ALL_VISITS, window: '\u00b11 day' },
  { name: 'Urinalysis', visits: ['Screening', 'C1/D1', 'C1/D15', 'Even-C/D1', 'Odd-C/D1', 'EOT', '1M-FUP'], window: '\u00b11 day' },
  { name: 'Pregnancy Test', visits: ['Screening', 'EOT'], window: 'Within 72 hrs' },
  { name: 'Tumor Biopsy', visits: ['Screening'], window: 'Within 28 days' },
  { name: 'CT/MRI Assessment', visits: ['Screening', 'Even-C/D1', 'EOT', '1M-FUP'], window: '\u00b17 days' },
  { name: 'Tumor Marker CEA', visits: ['Screening', 'Even-C/D1', 'EOT', '1M-FUP'], window: '\u00b13 days' },
  { name: 'PK Samples', visits: ['C1/D1', 'C1/D15', 'Even-C/D1', 'Even-C/D15', 'Odd-C/D15', 'EOT', '1M-FUP'], window: '\u00b115 min' },
  { name: 'ADA Sample', visits: ['C1/D1', 'Even-C/D15', 'Odd-C/D15', 'EOT', '1M-FUP'], window: 'Pre-dose' },
  { name: 'Biomarker Blood/Urine', visits: ['Screening', 'Even-C/D1', 'EOT'], window: '\u00b11 day' },
  { name: 'Skin Tox Prophylaxis', visits: ['C1/D1', 'C1/D8', 'C1/D15', 'C1/D22', 'Even-C/D1', 'Even-C/D15', 'Odd-C/D1', 'Odd-C/D15'], window: 'Pre-infusion' },
  { name: 'Infusion Reaction Prophylaxis', visits: ['C1/D1', 'C1/D15'], window: '30 min pre-infusion' },
  { name: 'Sym004 Infusion', visits: ['C1/D1', 'C1/D15', 'Even-C/D1', 'Even-C/D15', 'Odd-C/D1', 'Odd-C/D15'], window: 'Per protocol' },
  { name: 'FOLFIRI Infusion', visits: ['C1/D1', 'C1/D15', 'Even-C/D1', 'Even-C/D15', 'Odd-C/D1', 'Odd-C/D15'], window: 'Per protocol' },
  { name: 'Post-Infusion Monitoring', visits: ['C1/D1', 'C1/D15', 'Even-C/D1', 'Even-C/D15', 'Odd-C/D1', 'Odd-C/D15'], window: '1 hr post-infusion' },
  { name: 'Study Coordinator', visits: SYM004_ALL_VISITS, window: 'Each visit' },
  { name: 'Principal Investigator', visits: SYM004_ALL_VISITS, window: 'Each visit' },
  { name: 'Data Manager', visits: SYM004_ALL_VISITS, window: 'Each visit' },
];

const SYM004_CPT: CPTMapping[] = [
  { procedure: 'Informed Consent', cpt: '99205', description: 'New patient office visit — comprehensive', rates: { UCLA: 350, UCSF: 375, JHU: 340 } },
  { procedure: 'Baseline/Eligibility', cpt: '99215', description: 'Established patient office visit — comprehensive', rates: { UCLA: 275, UCSF: 290, JHU: 265 } },
  { procedure: 'Medication Survey', cpt: '99211', description: 'Established patient office visit — minimal', rates: { UCLA: 45, UCSF: 50, JHU: 42 } },
  { procedure: 'SAE Survey', cpt: '99211', description: 'Adverse event evaluation — minimal visit', rates: { UCLA: 45, UCSF: 50, JHU: 42 } },
  { procedure: 'DLT Evaluation', cpt: '99213', description: 'Established patient visit — low complexity', rates: { UCLA: 130, UCSF: 140, JHU: 125 } },
  { procedure: 'Vital Signs', cpt: '99173', description: 'Visual acuity / vital signs screening', rates: { UCLA: 35, UCSF: 38, JHU: 32 } },
  { procedure: 'ECOG PS', cpt: '99213', description: 'Performance status evaluation', rates: { UCLA: 85, UCSF: 90, JHU: 80 } },
  { procedure: 'Physical Exam', cpt: '99214', description: 'Office visit — moderate complexity', rates: { UCLA: 185, UCSF: 195, JHU: 175 } },
  { procedure: 'Dermatological Exam', cpt: '99213', description: 'Dermatological assessment — low complexity', rates: { UCLA: 120, UCSF: 130, JHU: 115 } },
  { procedure: 'ECG 12-lead', cpt: '93000', description: 'Electrocardiogram with interpretation', rates: { UCLA: 95, UCSF: 105, JHU: 90 } },
  { procedure: 'Safety Blood Panel', cpt: '80053', description: 'Comprehensive metabolic panel', rates: { UCLA: 65, UCSF: 72, JHU: 60 } },
  { procedure: 'Urinalysis', cpt: '81003', description: 'Urinalysis — automated with microscopy', rates: { UCLA: 25, UCSF: 28, JHU: 22 } },
  { procedure: 'Pregnancy Test', cpt: '81025', description: 'Urine pregnancy test', rates: { UCLA: 20, UCSF: 22, JHU: 18 } },
  { procedure: 'Tumor Biopsy', cpt: '88305', description: 'Surgical pathology — tissue exam', rates: { UCLA: 850, UCSF: 920, JHU: 800 } },
  { procedure: 'CT/MRI Assessment', cpt: '74178', description: 'CT abdomen and pelvis with contrast', rates: { UCLA: 1200, UCSF: 1350, JHU: 1150 } },
  { procedure: 'Tumor Marker CEA', cpt: '82378', description: 'Carcinoembryonic antigen (CEA)', rates: { UCLA: 75, UCSF: 82, JHU: 70 } },
  { procedure: 'PK Samples', cpt: '36415', description: 'Venipuncture — routine collection', rates: { UCLA: 40, UCSF: 45, JHU: 38 } },
  { procedure: 'ADA Sample', cpt: '86235', description: 'Anti-drug antibody assay', rates: { UCLA: 180, UCSF: 195, JHU: 170 } },
  { procedure: 'Biomarker Blood/Urine', cpt: '81479', description: 'Unlisted molecular pathology procedure', rates: { UCLA: 350, UCSF: 380, JHU: 330 } },
  { procedure: 'Skin Tox Prophylaxis', cpt: '96372', description: 'Therapeutic injection — subcutaneous', rates: { UCLA: 55, UCSF: 60, JHU: 50 } },
  { procedure: 'Infusion Reaction Prophylaxis', cpt: '96374', description: 'IV push — single substance', rates: { UCLA: 95, UCSF: 105, JHU: 90 } },
  { procedure: 'Sym004 Infusion', cpt: '96413', description: 'Chemotherapy IV infusion — first hour', rates: { UCLA: 450, UCSF: 485, JHU: 430 } },
  { procedure: 'FOLFIRI Infusion', cpt: '96415', description: 'Chemotherapy IV infusion — additional hour', rates: { UCLA: 380, UCSF: 410, JHU: 360 } },
  { procedure: 'Post-Infusion Monitoring', cpt: '99217', description: 'Observation care discharge', rates: { UCLA: 150, UCSF: 165, JHU: 140 } },
  { procedure: 'Study Coordinator', cpt: '99366', description: 'Team conference — direct contact', rates: { UCLA: 125, UCSF: 135, JHU: 120 } },
  { procedure: 'Principal Investigator', cpt: '99367', description: 'Team conference — physician 30 min', rates: { UCLA: 250, UCSF: 270, JHU: 240 } },
  { procedure: 'Data Manager', cpt: '99368', description: 'Team conference — non-physician 30 min', rates: { UCLA: 95, UCSF: 105, JHU: 90 } },
  { procedure: 'Central Lab Processing', cpt: '99000', description: 'Specimen handling and transport', rates: { UCLA: 35, UCSF: 40, JHU: 32 } },
];

function sym004CycleMultiplier(visit: string, numCycles: number): number {
  const remaining = Math.max(0, numCycles - 1);
  if (visit.startsWith('Even-C')) return Math.floor(remaining / 2);
  if (visit.startsWith('Odd-C')) return Math.ceil(remaining / 2);
  return 1;
}

const SYM004: ProtocolConfig = {
  id: 'sym004-09',
  trialName: 'Sym004-09',
  protocolTitle: 'Sym004 + FOLFIRI in mCRC',
  sponsor: 'Symphogen A/S',
  phase: '1b/2a',
  version: '4.0',
  lastUpdated: '22-Feb-2017',
  totalPatients: 50,
  patientLabel: '50 (3+3 + expansion)',
  visits: SYM004_VISITS,
  procedures: SYM004_PROCEDURES,
  cptMappings: SYM004_CPT,
  sites: [
    { key: 'UCLA', label: 'UCLA', patients: 20 },
    { key: 'UCSF', label: 'UCSF', patients: 15 },
    { key: 'JHU', label: 'Johns Hopkins', patients: 15 },
  ],
  siteSetup: 19000,
  monitoringCostPerVisit: 3000,
  monitoringVisitsPerCycle: 2,
  overheadRate: 0.15,
  dataManagementPerPatient: 1200,
  cycleOptions: [2, 4, 6, 8],
  defaultCycles: 4,
  getCycleMultiplier: sym004CycleMultiplier,
};

// ---------------------------------------------------------------------------
// Protocol Definitions — HERALD (CV-NCOV-004)
// ---------------------------------------------------------------------------
const HERALD_VISITS = [
  'V1 Day 1', 'V2 Day 29', 'V3 Phone Day 43', 'V4 Phone Day 57',
  'V5 Phone Day 120', 'V6 Clinic Day 211', 'V7 Phone Day 302', 'V8 Clinic Day 393',
];

const HERALD_PROCEDURES: SOAProcedure[] = [
  { name: 'Informed Consent', visits: ['V1 Day 1'], window: 'Before any study procedure' },
  { name: 'Eligibility Assessment', visits: ['V1 Day 1'], window: 'Day 1' },
  { name: 'Demographics', visits: ['V1 Day 1'], window: 'Day 1' },
  { name: 'Medical History', visits: ['V1 Day 1'], window: 'Day 1' },
  { name: 'Pregnancy Test', visits: ['V1 Day 1', 'V2 Day 29'], window: 'Pre-vaccination' },
  { name: 'Randomization', visits: ['V1 Day 1'], window: 'Day 1 pre-dose' },
  { name: 'Vaccination', visits: ['V1 Day 1', 'V2 Day 29'], window: 'Per protocol' },
  { name: 'Physical Exam', visits: ['V1 Day 1', 'V2 Day 29', 'V6 Clinic Day 211'], window: '\u00b12 days' },
  { name: 'Vital Signs', visits: ['V1 Day 1', 'V2 Day 29', 'V3 Phone Day 43', 'V6 Clinic Day 211'], window: '\u00b130 min' },
  { name: 'Antibody Blood Draw', visits: ['V1 Day 1', 'V2 Day 29', 'V6 Clinic Day 211'], window: 'Pre-vaccination' },
  { name: 'eDiary Setup', visits: ['V1 Day 1'], window: 'Post-vaccination' },
  { name: 'eDiary Review', visits: ['V2 Day 29', 'V3 Phone Day 43'], window: 'Each visit' },
  { name: 'Symptom Assessment', visits: ['V3 Phone Day 43'], window: 'Phone call' },
  { name: 'AE Collection', visits: ['V2 Day 29', 'V3 Phone Day 43', 'V4 Phone Day 57', 'V5 Phone Day 120', 'V6 Clinic Day 211', 'V7 Phone Day 302', 'V8 Clinic Day 393'], window: 'Each visit' },
  { name: 'SAE Review', visits: ['V1 Day 1', 'V2 Day 29', 'V4 Phone Day 57', 'V5 Phone Day 120', 'V6 Clinic Day 211', 'V7 Phone Day 302', 'V8 Clinic Day 393'], window: 'Each visit' },
  { name: 'Concomitant Meds', visits: ['V1 Day 1', 'V2 Day 29', 'V4 Phone Day 57', 'V5 Phone Day 120', 'V6 Clinic Day 211', 'V8 Clinic Day 393'], window: 'Each visit' },
  { name: 'COVID Detection', visits: HERALD_VISITS, window: 'Each contact' },
  { name: 'Trial End Assessment', visits: ['V8 Clinic Day 393'], window: 'Final visit' },
  { name: 'Study Coordinator', visits: HERALD_VISITS, window: 'Each visit' },
  { name: 'Principal Investigator', visits: ['V1 Day 1', 'V2 Day 29', 'V6 Clinic Day 211', 'V8 Clinic Day 393'], window: 'Clinic visits' },
  { name: 'Data Manager', visits: HERALD_VISITS, window: 'Each visit' },
];

const HERALD_CPT: CPTMapping[] = [
  { procedure: 'Informed Consent', cpt: '99205', description: 'New patient office visit — comprehensive', rates: { 'Site Avg': 285 } },
  { procedure: 'Eligibility Assessment', cpt: '99215', description: 'Established patient visit — comprehensive', rates: { 'Site Avg': 220 } },
  { procedure: 'Demographics', cpt: '99211', description: 'Minimal office visit — data collection', rates: { 'Site Avg': 35 } },
  { procedure: 'Medical History', cpt: '99213', description: 'Office visit — low complexity', rates: { 'Site Avg': 95 } },
  { procedure: 'Pregnancy Test', cpt: '81025', description: 'Urine pregnancy test', rates: { 'Site Avg': 18 } },
  { procedure: 'Randomization', cpt: '99211', description: 'Randomization administration', rates: { 'Site Avg': 40 } },
  { procedure: 'Vaccination', cpt: '90471', description: 'Immunization administration', rates: { 'Site Avg': 55 } },
  { procedure: 'Physical Exam', cpt: '99214', description: 'Office visit — moderate complexity', rates: { 'Site Avg': 155 } },
  { procedure: 'Vital Signs', cpt: '99173', description: 'Vital signs screening', rates: { 'Site Avg': 28 } },
  { procedure: 'Antibody Blood Draw', cpt: '36415', description: 'Venipuncture — routine collection', rates: { 'Site Avg': 32 } },
  { procedure: 'eDiary Setup', cpt: '99211', description: 'Patient device training', rates: { 'Site Avg': 45 } },
  { procedure: 'eDiary Review', cpt: '99211', description: 'Electronic diary review — minimal', rates: { 'Site Avg': 30 } },
  { procedure: 'Symptom Assessment', cpt: '99211', description: 'Telephone symptom assessment', rates: { 'Site Avg': 30 } },
  { procedure: 'AE Collection', cpt: '99213', description: 'Adverse event evaluation', rates: { 'Site Avg': 85 } },
  { procedure: 'SAE Review', cpt: '99213', description: 'Serious adverse event review', rates: { 'Site Avg': 95 } },
  { procedure: 'Concomitant Meds', cpt: '99211', description: 'Medication review — minimal', rates: { 'Site Avg': 30 } },
  { procedure: 'COVID Detection', cpt: '87635', description: 'SARS-CoV-2 nucleic acid detection', rates: { 'Site Avg': 75 } },
  { procedure: 'Trial End Assessment', cpt: '99215', description: 'Comprehensive final visit', rates: { 'Site Avg': 220 } },
  { procedure: 'Study Coordinator', cpt: '99366', description: 'Team conference — direct contact', rates: { 'Site Avg': 95 } },
  { procedure: 'Principal Investigator', cpt: '99367', description: 'Team conference — physician 30 min', rates: { 'Site Avg': 200 } },
  { procedure: 'Data Manager', cpt: '99368', description: 'Team conference — non-physician 30 min', rates: { 'Site Avg': 75 } },
];

const HERALD: ProtocolConfig = {
  id: 'herald',
  trialName: 'CV-NCOV-004 HERALD',
  protocolTitle: 'HERALD — COVID-19 mRNA Vaccine Phase 2b/3',
  sponsor: 'CureVac SE',
  phase: '2b/3',
  version: '3.0',
  lastUpdated: '15-Mar-2021',
  totalPatients: 35300,
  patientLabel: '35,300 (randomized 1:1)',
  visits: HERALD_VISITS,
  procedures: HERALD_PROCEDURES,
  cptMappings: HERALD_CPT,
  sites: [
    { key: 'Site Avg', label: 'Site Average (200 sites)', patients: 35300 },
  ],
  siteSetup: 25000,
  monitoringCostPerVisit: 600,
  monitoringVisitsPerCycle: 1,
  overheadRate: 0.12,
  dataManagementPerPatient: 450,
  cycleOptions: [1],
  defaultCycles: 1,
  getCycleMultiplier: () => 1,
};

// ---------------------------------------------------------------------------
// Protocol Registry
// ---------------------------------------------------------------------------
const BUILTIN_PROTOCOLS: Record<string, ProtocolConfig> = {
  'sym004-09': SYM004,
  'herald': HERALD,
};

const BUILTIN_INDICATION_MAP: Record<string, string> = {
  'sym004-09': 'Oncology',
  'herald': 'Vaccine',
};

// ---------------------------------------------------------------------------
// Builtin SOA Grids — for Calculate Budget without PDF import
// ---------------------------------------------------------------------------
const BUILTIN_SOA_GRIDS: Record<string, Record<string, Record<string, boolean>>> = {
  'sym004-09': {
    "Informed Consent":       {"Screening":true,"C1/D1":false,"C1/D8":false,"C1/D15":false,"C1/D22":false,"Even-C/D1":false,"Even-C/D15":false,"Odd-C/D1":false,"Odd-C/D15":false,"EOT":false,"1M-FUP":false},
    "Vital Signs":            {"Screening":true,"C1/D1":true,"C1/D8":true,"C1/D15":true,"C1/D22":true,"Even-C/D1":true,"Even-C/D15":true,"Odd-C/D1":true,"Odd-C/D15":true,"EOT":true,"1M-FUP":true},
    "Physical Examination":   {"Screening":true,"C1/D1":true,"C1/D8":false,"C1/D15":false,"C1/D22":false,"Even-C/D1":true,"Even-C/D15":false,"Odd-C/D1":true,"Odd-C/D15":false,"EOT":true,"1M-FUP":true},
    "ECOG Performance Status":{"Screening":true,"C1/D1":true,"C1/D8":false,"C1/D15":false,"C1/D22":false,"Even-C/D1":true,"Even-C/D15":false,"Odd-C/D1":true,"Odd-C/D15":false,"EOT":true,"1M-FUP":true},
    "Dermatological Examination":{"Screening":true,"C1/D1":true,"C1/D8":false,"C1/D15":true,"C1/D22":false,"Even-C/D1":true,"Even-C/D15":true,"Odd-C/D1":true,"Odd-C/D15":true,"EOT":true,"1M-FUP":false},
    "ECG":                    {"Screening":true,"C1/D1":false,"C1/D8":false,"C1/D15":false,"C1/D22":false,"Even-C/D1":false,"Even-C/D15":false,"Odd-C/D1":false,"Odd-C/D15":false,"EOT":true,"1M-FUP":false},
    "Safety Blood Samples":   {"Screening":true,"C1/D1":true,"C1/D8":true,"C1/D15":true,"C1/D22":true,"Even-C/D1":true,"Even-C/D15":true,"Odd-C/D1":true,"Odd-C/D15":true,"EOT":true,"1M-FUP":true},
    "Urinalysis":             {"Screening":true,"C1/D1":true,"C1/D8":false,"C1/D15":true,"C1/D22":false,"Even-C/D1":true,"Even-C/D15":false,"Odd-C/D1":true,"Odd-C/D15":false,"EOT":true,"1M-FUP":true},
    "Pregnancy Test":         {"Screening":true,"C1/D1":false,"C1/D8":false,"C1/D15":false,"C1/D22":false,"Even-C/D1":false,"Even-C/D15":false,"Odd-C/D1":false,"Odd-C/D15":false,"EOT":true,"1M-FUP":false},
    "Tumor Biopsy":           {"Screening":true,"C1/D1":false,"C1/D8":false,"C1/D15":false,"C1/D22":false,"Even-C/D1":false,"Even-C/D15":false,"Odd-C/D1":false,"Odd-C/D15":false,"EOT":false,"1M-FUP":false},
    "CT Scan":                {"Screening":true,"C1/D1":false,"C1/D8":false,"C1/D15":false,"C1/D22":false,"Even-C/D1":true,"Even-C/D15":false,"Odd-C/D1":false,"Odd-C/D15":false,"EOT":true,"1M-FUP":true},
    "Tumor Marker":           {"Screening":true,"C1/D1":false,"C1/D8":false,"C1/D15":false,"C1/D22":false,"Even-C/D1":true,"Even-C/D15":false,"Odd-C/D1":false,"Odd-C/D15":false,"EOT":true,"1M-FUP":true},
    "PK Sample":              {"Screening":false,"C1/D1":true,"C1/D8":true,"C1/D15":true,"C1/D22":true,"Even-C/D1":true,"Even-C/D15":false,"Odd-C/D1":true,"Odd-C/D15":false,"EOT":false,"1M-FUP":false},
    "ADA Sample":             {"Screening":true,"C1/D1":false,"C1/D8":false,"C1/D15":false,"C1/D22":false,"Even-C/D1":true,"Even-C/D15":false,"Odd-C/D1":true,"Odd-C/D15":false,"EOT":true,"1M-FUP":false},
    "Drug Administration":    {"Screening":false,"C1/D1":true,"C1/D8":true,"C1/D15":true,"C1/D22":true,"Even-C/D1":true,"Even-C/D15":true,"Odd-C/D1":true,"Odd-C/D15":true,"EOT":false,"1M-FUP":false},
    "Medication Survey":      {"Screening":true,"C1/D1":true,"C1/D8":true,"C1/D15":true,"C1/D22":true,"Even-C/D1":true,"Even-C/D15":true,"Odd-C/D1":true,"Odd-C/D15":true,"EOT":true,"1M-FUP":true},
    "SAE Survey":             {"Screening":true,"C1/D1":true,"C1/D8":true,"C1/D15":true,"C1/D22":true,"Even-C/D1":true,"Even-C/D15":true,"Odd-C/D1":true,"Odd-C/D15":true,"EOT":true,"1M-FUP":true},
  },
};

// Default CPT rate for imported procedure names that get a fuzzy match
const DEFAULT_IMPORTED_RATE = 100;

// CPT lookup table for common procedure keywords (used for imported protocols)
const IMPORT_CPT_LOOKUP: Record<string, { cpt: string; desc: string; rate: number }> = {
  'consent': { cpt: '99205', desc: 'Office visit — comprehensive', rate: 285 },
  'eligibility': { cpt: '99215', desc: 'Established patient visit', rate: 220 },
  'vital': { cpt: '99173', desc: 'Vital signs screening', rate: 32 },
  'physical': { cpt: '99214', desc: 'Office visit — moderate complexity', rate: 165 },
  'ecg': { cpt: '93000', desc: 'Electrocardiogram', rate: 95 },
  'electrocardiogram': { cpt: '93000', desc: 'Electrocardiogram', rate: 95 },
  'blood': { cpt: '36415', desc: 'Venipuncture — routine', rate: 38 },
  'cbc': { cpt: '85025', desc: 'Complete blood count', rate: 50 },
  'chemistry': { cpt: '80053', desc: 'Comprehensive metabolic panel', rate: 60 },
  'urinalysis': { cpt: '81001', desc: 'Urinalysis with microscopy', rate: 22 },
  'pregnancy': { cpt: '81025', desc: 'Urine pregnancy test', rate: 18 },
  'ct': { cpt: '74177', desc: 'CT with contrast', rate: 1100 },
  'mri': { cpt: '70553', desc: 'MRI with contrast', rate: 1300 },
  'biopsy': { cpt: '88305', desc: 'Surgical pathology', rate: 800 },
  'infusion': { cpt: '96413', desc: 'IV infusion — first hour', rate: 420 },
  'injection': { cpt: '96372', desc: 'Therapeutic injection', rate: 52 },
  'vaccination': { cpt: '90471', desc: 'Immunization administration', rate: 55 },
  'vaccine': { cpt: '90471', desc: 'Immunization administration', rate: 55 },
  'pk': { cpt: '36415', desc: 'Venipuncture — PK sample', rate: 40 },
  'ada': { cpt: '86235', desc: 'Anti-drug antibody assay', rate: 175 },
  'biomarker': { cpt: '81479', desc: 'Molecular pathology', rate: 340 },
  'coordinator': { cpt: '99366', desc: 'Team conference — direct', rate: 110 },
  'investigator': { cpt: '99367', desc: 'Team conference — physician', rate: 225 },
  'data manager': { cpt: '99368', desc: 'Team conference — non-physician', rate: 85 },
  'adverse': { cpt: '99213', desc: 'AE evaluation', rate: 90 },
  'sae': { cpt: '99213', desc: 'SAE review', rate: 95 },
  'concomitant': { cpt: '99211', desc: 'Medication review', rate: 30 },
  'medication': { cpt: '99211', desc: 'Medication review', rate: 35 },
};

function lookupCPTForProcedure(procName: string): { cpt: string; desc: string; rate: number } {
  const lower = procName.toLowerCase();
  for (const [keyword, entry] of Object.entries(IMPORT_CPT_LOOKUP)) {
    if (lower.includes(keyword)) return entry;
  }
  return { cpt: '99211', desc: 'Office visit — minimal', rate: DEFAULT_IMPORTED_RATE };
}

/** Convert a ParsedProtocol (from PDF upload) into a full ProtocolConfig */
function convertParsedToConfig(parsed: ParsedProtocol): ProtocolConfig {
  // Normalize visits — handle both string[] and {label, day_range}[] from backend
  const visitLabels: string[] = parsed.visits.map((v: any) => (typeof v === 'string' ? v : v.label || String(v)));

  // Build SOAProcedure[] from the parsed soa_grid
  const procedures: SOAProcedure[] = parsed.procedures.map((procName) => {
    const visitList = visitLabels.filter((v) => parsed.soa_grid[procName]?.[v]);
    return { name: procName, visits: visitList.length > 0 ? visitList : visitLabels, window: 'Per protocol' };
  });

  // Auto-generate CPT mappings
  const cptMappings: CPTMapping[] = parsed.procedures.map((procName) => {
    const match = lookupCPTForProcedure(procName);
    return { procedure: procName, cpt: match.cpt, description: match.desc, rates: { 'Site Avg': match.rate } };
  });

  // Add standard staff lines if not already present
  const hasCoordinator = parsed.procedures.some((p) => p.toLowerCase().includes('coordinator'));
  const hasPI = parsed.procedures.some((p) => p.toLowerCase().includes('investigator'));
  const hasDM = parsed.procedures.some((p) => p.toLowerCase().includes('data manager'));
  if (!hasCoordinator) {
    procedures.push({ name: 'Study Coordinator', visits: visitLabels, window: 'Each visit' });
    cptMappings.push({ procedure: 'Study Coordinator', cpt: '99366', description: 'Team conference — direct', rates: { 'Site Avg': 110 } });
  }
  if (!hasPI) {
    procedures.push({ name: 'Principal Investigator', visits: visitLabels, window: 'Each visit' });
    cptMappings.push({ procedure: 'Principal Investigator', cpt: '99367', description: 'Team conference — physician', rates: { 'Site Avg': 225 } });
  }
  if (!hasDM) {
    procedures.push({ name: 'Data Manager', visits: visitLabels, window: 'Each visit' });
    cptMappings.push({ procedure: 'Data Manager', cpt: '99368', description: 'Team conference — non-physician', rates: { 'Site Avg': 85 } });
  }

  const nPat = parsed.n_patients || 50;

  return {
    id: parsed.protocol_id.toLowerCase(),
    trialName: parsed.trial_name || parsed.protocol_id,
    protocolTitle: parsed.trial_name || 'Imported Protocol',
    sponsor: parsed.sponsor || 'Unknown Sponsor',
    phase: parsed.phase || 'Unknown',
    version: '1.0',
    lastUpdated: new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
    totalPatients: nPat,
    patientLabel: `${nPat} (from protocol)`,
    visits: visitLabels,
    procedures,
    cptMappings,
    sites: [{ key: 'Site Avg', label: 'Site Average', patients: nPat }],
    siteSetup: 19000,
    monitoringCostPerVisit: 3000,
    monitoringVisitsPerCycle: 1,
    overheadRate: 0.15,
    dataManagementPerPatient: 1200,
    cycleOptions: [1],
    defaultCycles: 1,
    getCycleMultiplier: () => 1,
  };
}

const STORAGE_KEY = 'd3ri_imported_protocols';

function loadImportedProtocols(): Record<string, ProtocolConfig> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const entries: Array<{ id: string; config: any }> = JSON.parse(raw);
    const result: Record<string, ProtocolConfig> = {};
    for (const entry of entries) {
      // Restore the getCycleMultiplier function (lost during JSON serialization)
      result[entry.id] = { ...entry.config, getCycleMultiplier: () => 1 };
    }
    return result;
  } catch {
    return {};
  }
}

function saveImportedProtocols(protocols: Record<string, ProtocolConfig>) {
  const entries = Object.entries(protocols).map(([id, config]) => {
    // Strip the function before serializing
    const { getCycleMultiplier, ...rest } = config;
    return { id, config: rest };
  });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const GLOSSARY_ENTRIES: GlossaryEntry[] = [
  { term: 'CPT Code', definition: 'Current Procedural Terminology code \u2014 standardized numeric code assigned by the AMA to describe medical, surgical, and diagnostic services for billing purposes.' },
  { term: 'SOA Grid', definition: 'Schedule of Assessments grid \u2014 protocol-defined matrix listing every clinical procedure against each study visit to show when assessments are required.' },
  { term: 'Per-Patient Cost', definition: 'Total cost of all procedures, visits, and assessments for a single study subject across the entire trial duration, excluding site-level overhead.' },
  { term: 'Site Setup', definition: 'One-time cost per investigative site covering IRB submission, site qualification, staff training, equipment calibration, and regulatory startup.' },
  { term: 'Overhead', definition: 'Institutional indirect cost rate applied on top of direct clinical costs, typically 10\u201325%, covering facilities, administration, and institutional support services.' },
  { term: 'Budget Variance', definition: 'Percentage difference between budgeted and actual trial expenditure. Green (<5%), amber (5\u201315%), red (>15%) thresholds trigger escalating review.' },
  { term: 'RP2D', definition: 'Recommended Phase 2 Dose \u2014 the dose selected from Phase 1 dose escalation for evaluation in Phase 2 efficacy trials.' },
  { term: 'mCRC', definition: 'Metastatic colorectal cancer \u2014 colorectal cancer that has spread beyond the colon or rectum to distant organs.' },
  { term: 'FOLFIRI', definition: 'Chemotherapy regimen combining folinic acid (leucovorin), fluorouracil (5-FU), and irinotecan. Standard second-line treatment for mCRC.' },
  { term: 'EGFR', definition: 'Epidermal Growth Factor Receptor \u2014 a transmembrane protein targeted by Sym004 (mix of two anti-EGFR antibodies futuximab and modotuximab).' },
  { term: 'DLT', definition: 'Dose Limiting Toxicity \u2014 a pre-defined adverse event of sufficient severity (typically CTCAE Grade \u22653) to limit further dose escalation.' },
  { term: 'EOT', definition: 'End of Treatment \u2014 the final visit at which study drug is administered, followed by safety assessments and transition to follow-up.' },
  { term: '1M FUP', definition: 'One-Month Follow-Up \u2014 the safety follow-up visit occurring approximately 28 days after the last dose of study drug.' },
  { term: 'ECOG PS', definition: 'Eastern Cooperative Oncology Group Performance Status \u2014 scale from 0 (fully active) to 5 (dead) used to assess patient functional status.' },
  { term: 'ADA', definition: 'Anti-Drug Antibody \u2014 immunoglobulin produced by the patient in response to the study drug, which may reduce efficacy or cause hypersensitivity.' },
  { term: 'PK', definition: 'Pharmacokinetics \u2014 the study of how the body absorbs, distributes, metabolizes, and excretes a drug over time.' },
  { term: 'CEA', definition: 'Carcinoembryonic Antigen \u2014 a tumor marker protein elevated in colorectal cancer, used to monitor disease progression and treatment response.' },
  { term: 'HERALD', definition: 'CureVac Phase 2b/3 efficacy trial (CV-NCOV-004) evaluating CVnCoV mRNA vaccine against COVID-19 across 200+ sites globally.' },
];

const PIE_COLORS = ['#4299e1', '#48bb78', '#ecc94b', '#f56565'];

const EXISTING_PROTOCOLS: ImportedProtocol[] = [
  { id: 'sym004-09', name: 'Sym004-09 v4.0', sponsor: 'Symphogen A/S', version: '4.0', status: 'Active', lastUpdated: '2/22/2017' },
  { id: 'herald', name: 'CV-NCOV-004 HERALD v3.0', sponsor: 'CureVac SE', version: '3.0', status: 'Active', lastUpdated: '3/15/2021' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function fmt(n: number): string {
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function getCPTRate(procedure: string, siteKey: string, mappings: CPTMapping[], overrides: Record<string, Record<string, number>>): number {
  if (overrides[procedure]?.[siteKey] !== undefined) {
    return overrides[procedure][siteKey];
  }
  const mapping = mappings.find((m) => m.procedure === procedure);
  return mapping?.rates[siteKey] ?? 0;
}

function getVisitProcedures(visit: string, procedures: SOAProcedure[]): SOAProcedure[] {
  return procedures.filter((p) => p.visits.includes(visit));
}

function computeVisitCost(visit: string, siteKey: string, procedures: SOAProcedure[], mappings: CPTMapping[], overrides: Record<string, Record<string, number>>): number {
  const procs = getVisitProcedures(visit, procedures);
  return procs.reduce((sum, p) => sum + getCPTRate(p.name, siteKey, mappings, overrides), 0);
}

function computeProtocolBudget(protocol: ProtocolConfig, siteKey: string, numCycles: number, overrides: Record<string, Record<string, number>>) {
  let treatment = 0;
  protocol.visits.forEach((visit) => {
    const cost = computeVisitCost(visit, siteKey, protocol.procedures, protocol.cptMappings, overrides);
    const mult = protocol.getCycleMultiplier(visit, numCycles);
    treatment += cost * mult;
  });
  const overhead = treatment * protocol.overheadRate;
  return treatment + protocol.siteSetup + overhead;
}

function computeTrialTotal(protocol: ProtocolConfig, numCycles: number, overrides: Record<string, Record<string, number>>) {
  let total = 0;
  for (const site of protocol.sites) {
    const perPatient = computeProtocolBudget(protocol, site.key, numCycles, overrides);
    const monitoringVisits = numCycles * protocol.monitoringVisitsPerCycle;
    const monitoring = monitoringVisits * protocol.monitoringCostPerVisit;
    const dataManagement = site.patients * protocol.dataManagementPerPatient;
    total += perPatient * site.patients + protocol.siteSetup + monitoring + dataManagement;
  }
  return total;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function BudgetCalculator() {
  const [activeTab, setActiveTab] = useState<TabKey>('calcbudget');
  const [selectedProtocolId, setSelectedProtocolId] = useState<string>('sym004-09');
  const [selectedSiteKey, setSelectedSiteKey] = useState<string>('UCLA');
  const [numCycles, setNumCycles] = useState<number>(4);
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [rateOverrides, setRateOverrides] = useState<Record<string, Record<string, number>>>({});

  // Mutable protocol registry: builtins + localStorage imports
  const [userProtocols, setUserProtocols] = useState<Record<string, ProtocolConfig>>(() => loadImportedProtocols());
  const allProtocols = useMemo<Record<string, ProtocolConfig>>(() => ({ ...BUILTIN_PROTOCOLS, ...userProtocols }), [userProtocols]);

  // Import Protocol state
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [parsedResult, setParsedResult] = useState<ParsedProtocol | null>(null);
  const [uploadError, setUploadError] = useState<string>('');
  const [importedProtocols, setImportedProtocols] = useState<ImportedProtocol[]>(EXISTING_PROTOCOLS);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);

  // Backend warmup — wake Render from cold start on page load
  const [backendReady, setBackendReady] = useState(false);
  const [warmingUp, setWarmingUp] = useState(true);

  // Show "Connecting to server..." for 3 seconds on mount, then hide
  useEffect(() => {
    const timer = setTimeout(() => setWarmingUp(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Wake the Render backend on mount — hit /api/v1/health first (fast),
  // then confirm with /api/v1/protocol/health (heavier, loads pdfplumber)
  useEffect(() => {
    let cancelled = false;
    const API_BASE = process.env.REACT_APP_API_URL || '';
    const ping = async () => {
      // Silent wake-up ping to main health endpoint
      const wakeUrl = `${API_BASE}/api/v1/health`;
      console.log('[d3ri-upload] Wake ping: %s', wakeUrl);
      fetch(wakeUrl).catch(() => {});

      // Protocol health check (confirms full readiness)
      const healthUrl = `${API_BASE}/api/v1/protocol/health`;
      console.log('[d3ri-upload] Health check: %s  API_BASE=%s', healthUrl, JSON.stringify(API_BASE));
      try {
        const res = await fetch(healthUrl);
        console.log('[d3ri-upload] Health check response: status=%d ok=%s', res.status, res.ok);
        if (!cancelled && res.ok) setBackendReady(true);
      } catch (err) {
        console.warn('[d3ri-upload] Health check failed (will retry in 3s):', err);
        if (!cancelled) setTimeout(async () => {
          try {
            const res = await fetch(healthUrl);
            console.log('[d3ri-upload] Health check retry response: status=%d ok=%s', res.status, res.ok);
            if (!cancelled && res.ok) setBackendReady(true);
          } catch (retryErr) { console.error('[d3ri-upload] Health check retry also failed — uploads will be blocked:', retryErr); }
        }, 3000);
      }
    };
    ping();
    return () => { cancelled = true; };
  }, []);

  // Calculate Budget state
  const [calcBudgetResult, setCalcBudgetResult] = useState<any>(null);
  const [calcBudgetLoading, setCalcBudgetLoading] = useState(false);
  const [calcBudgetError, setCalcBudgetError] = useState<string>('');
  const [calcNPatients, setCalcNPatients] = useState<number>(50);
  const [calcNSites, setCalcNSites] = useState<number>(3);
  const [calcDurationMonths, setCalcDurationMonths] = useState<number>(24);
  const [calcOverheadPct, setCalcOverheadPct] = useState<number>(18);
  const [calcRateOverrides, setCalcRateOverrides] = useState<Record<string, number>>({});
  const [calcEditingRate, setCalcEditingRate] = useState<string | null>(null);
  const [calcShowOverrides, setCalcShowOverrides] = useState(false);
  const [calcUnmappedRates, setCalcUnmappedRates] = useState<Record<string, number>>({});

  // Support ?tab=import deep link from Protocol Setup page
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab');
      if (tab === 'import') setActiveTab('import');
    } catch { /* ignore */ }
  }, []);

  const protocol = allProtocols[selectedProtocolId] || SYM004;

  // Derive dropdown list from allProtocols
  const protocolList = useMemo(() => {
    const items = Object.values(allProtocols).map((p) => ({
      id: p.id,
      label: `${p.trialName} \u2014 ${p.sponsor} \u2014 Phase ${p.phase}`,
    }));
    items.push({ id: '__import__', label: '+ Import New Protocol' });
    return items;
  }, [allProtocols]);

  // Derive indication map
  const indicationMap = useMemo(() => {
    const map: Record<string, string> = { ...BUILTIN_INDICATION_MAP };
    for (const [id, p] of Object.entries(userProtocols)) {
      map[id] = p.phase.includes('1') ? 'Oncology' : 'Clinical Trial';
    }
    return map;
  }, [userProtocols]);

  const handleProtocolChange = (id: string) => {
    if (id === '__import__') {
      setActiveTab('import');
      return;
    }
    setSelectedProtocolId(id);
    const p = allProtocols[id];
    if (p) {
      setSelectedSiteKey(p.sites[0]?.key ?? '');
      setNumCycles(p.defaultCycles);
      setRateOverrides({});
    }
    // Reset calculate budget state
    setCalcBudgetResult(null);
    setCalcBudgetError('');
    setCalcRateOverrides({});
    setCalcUnmappedRates({});
    setCalcNPatients(allProtocols[id]?.totalPatients ?? 50);
  };

  const addImportedProtocol = (config: ProtocolConfig) => {
    setUserProtocols((prev) => {
      const updated = { ...prev, [config.id]: config };
      saveImportedProtocols(updated);
      return updated;
    });
  };

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'calcbudget', label: 'Calculate Budget' },
    { key: 'perpatient', label: 'Per-Patient Budget' },
    { key: 'summary', label: 'Trial Summary' },
    ...(Object.keys(allProtocols).length >= 2 ? [{ key: 'compare' as TabKey, label: 'Compare' }] : []),
    { key: 'soa', label: 'SOA Grid' },
    { key: 'cpt', label: 'CPT Mapping' },
    { key: 'import', label: 'Import Protocol' },
    { key: 'forecast', label: 'Forecast' },
  ];

  // Compute per-patient cost for each site
  const perPatientCost = useMemo(() => {
    const result: Record<string, number> = {};
    for (const site of protocol.sites) {
      result[site.key] = computeProtocolBudget(protocol, site.key, numCycles, rateOverrides);
    }
    return result;
  }, [protocol, numCycles, rateOverrides]);

  // Visit cost breakdown
  const visitBreakdown = useMemo(() => {
    const siteKey = selectedSiteKey === 'ALL' ? protocol.sites[0]?.key ?? '' : selectedSiteKey;
    let cumulative = 0;
    return protocol.visits.map((visit) => {
      const baseCost = computeVisitCost(visit, siteKey, protocol.procedures, protocol.cptMappings, rateOverrides);
      const mult = protocol.getCycleMultiplier(visit, numCycles);
      const totalCost = baseCost * mult;
      cumulative += totalCost;
      const procCount = getVisitProcedures(visit, protocol.procedures).length;
      return { visit, baseCost, mult, totalCost, cumulative, procCount };
    });
  }, [protocol, selectedSiteKey, numCycles, rateOverrides]);

  const treatmentSubtotal = useMemo(() => visitBreakdown.reduce((s, v) => s + v.totalCost, 0), [visitBreakdown]);
  const overheadAmount = treatmentSubtotal * protocol.overheadRate;
  const totalPerPatient = treatmentSubtotal + protocol.siteSetup + overheadAmount;

  // ---------------------------------------------------------------------------
  // Styles
  // ---------------------------------------------------------------------------
  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '8px 16px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    backgroundColor: active ? '#2b6cb0' : '#171923',
    color: active ? '#fff' : '#a0aec0',
  });

  const cardStyle: React.CSSProperties = {
    backgroundColor: '#1a202c',
    border: '1px solid #2d3748',
    borderRadius: 8,
    padding: '16px 20px',
  };

  const thStyle: React.CSSProperties = {
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: '#a0aec0',
    fontSize: 11,
    fontWeight: 600,
    padding: '8px 12px',
    borderBottom: '2px solid #2d3748',
    textAlign: 'left',
  };

  const tdStyle: React.CSSProperties = {
    padding: '10px 12px',
    borderBottom: '1px solid #1a365d',
    fontSize: 13,
    color: '#e2e8f0',
  };

  const badgeStyle = (variant: 'green' | 'amber' | 'red' | 'blue'): React.CSSProperties => {
    const map = {
      green: { color: '#48bb78', backgroundColor: '#22543d' },
      amber: { color: '#ecc94b', backgroundColor: '#744210' },
      red: { color: '#f56565', backgroundColor: '#742a2a' },
      blue: { color: '#63b3ed', backgroundColor: '#1a365d' },
    };
    return { borderRadius: 12, padding: '2px 8px', fontWeight: 600, fontSize: 11, ...map[variant] };
  };

  // Compute a quick per-patient figure for the stat pills
  const quickPerPatient = useMemo(() => {
    const siteKey = protocol.sites[0]?.key ?? '';
    return computeProtocolBudget(protocol, siteKey, protocol.defaultCycles, rateOverrides);
  }, [protocol, rateOverrides]);

  // ---------------------------------------------------------------------------
  // Protocol Upload Handler
  // ---------------------------------------------------------------------------
  const handleFileUpload = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setUploadError('Only PDF files are accepted.');
      setUploadStatus('error');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setUploadError('File exceeds 50 MB limit.');
      setUploadStatus('error');
      return;
    }
    setUploadStatus('uploading');
    setUploadError('');
    setUploadProgress(0);

    try {
      const uploadUrl = '/api/v1/protocol/parse';
      console.log('[upload] Sending PDF to:', uploadUrl, 'file:', file.name, 'size:', file.size);

      // --- Step 1: upload PDF binary, get job_id back (202 Accepted) ---
      const formData = new FormData();
      formData.append('file', file);
      let submitRes: Response;
      try {
        submitRes = await fetch(uploadUrl, { method: 'POST', body: formData });
      } catch (networkErr: any) {
        console.error('[upload] Network error — fetch to %s failed:', uploadUrl, networkErr);
        throw new Error(`Network error: could not reach backend. ${networkErr.message || ''}`);
      }
      console.log('[upload] Response status:', submitRes.status);
      if (submitRes.status !== 202) {
        const errBody = await submitRes.text().catch(() => '');
        console.log('[upload] Response body:', errBody);
        const errData = (() => { try { return JSON.parse(errBody); } catch { return {}; } })();
        throw new Error(errData.detail || `Upload failed (HTTP ${submitRes.status}). ${errBody.slice(0, 200)}`);
      }
      const submitBody = await submitRes.json();
      const job_id: string | undefined = submitBody.job_id;
      console.log('[upload] Response body:', JSON.stringify(submitBody));
      if (!job_id) {
        throw new Error('Backend returned 202 but no job_id. Response: ' + JSON.stringify(submitBody));
      }
      console.log('[upload] Job accepted: job_id=%s — starting poll loop', job_id);
      setUploadProgress(5);

      // --- Step 2: poll parse-status every 3 seconds ---
      const pollUrl = `/api/v1/protocol/parse-status/${job_id}`;
      console.log('[upload] Poll URL:', pollUrl);

      const result: any = await new Promise((resolve, reject) => {
        let pollCount = 0;
        const maxPolls = 120; // 6 minutes max

        const tick = async () => {
          pollCount++;
          try {
            const statusRes = await fetch(pollUrl);
            if (!statusRes.ok) {
              const body = await statusRes.text().catch(() => '');
              console.error('[upload] Poll GET failed: status=%d body=%s', statusRes.status, body);
              throw new Error(`Status check failed (HTTP ${statusRes.status})`);
            }
            const statusData = await statusRes.json();
            console.log('[upload] Poll #%d: progress=%d status=%s phase=%s', pollCount, statusData.progress, statusData.status, statusData.phase);
            setUploadProgress(statusData.progress ?? 0);

            if (statusData.status === 'complete') {
              clearInterval(timer);
              console.log('[upload] Parse complete after %d polls', pollCount);
              resolve(statusData);
            } else if (statusData.status === 'failed') {
              clearInterval(timer);
              const errMsg = statusData.error || 'PDF parsing failed on server.';
              console.error('[upload] Server parse failed:', errMsg);
              reject(new Error(errMsg));
            } else if (pollCount >= maxPolls) {
              clearInterval(timer);
              reject(new Error('Parsing timed out after 6 minutes.'));
            }
          } catch (e) {
            clearInterval(timer);
            console.error('[upload] Poll exception:', e);
            reject(e);
          }
        };

        tick(); // immediate first poll
        const timer = setInterval(tick, 3000);
      });

      setUploadProgress(100);

      // Debug: log exactly what the backend returned
      console.log('[parse-complete] result keys:', Object.keys(result));
      console.log('[parse-complete] result.data keys:', result.data ? Object.keys(result.data) : 'NO DATA');
      console.log('[parse-complete] visits:', result.data?.visits?.length, result.data?.visits);
      console.log('[parse-complete] procedures:', result.data?.procedures?.length);
      console.log('[parse-complete] soa_grid keys:', Object.keys(result.data?.soa_grid || {}));
      console.log('[parse-complete] trial_name:', result.data?.trial_name);
      console.log('[parse-complete] confidence_score:', result.data?.confidence_score);

      // Guard: if result.data is missing or has no visits, show error
      if (!result.data || !result.data.visits || result.data.visits.length === 0) {
        console.error('[parse-complete] Backend returned empty parse result:', JSON.stringify(result).slice(0, 500));
        throw new Error(`Parse completed but returned no visits. Confidence: ${result.data?.confidence_score ?? 'N/A'}. ${(result.data?.manual_review_needed || []).join('; ')}`);
      }

      setParsedResult(result.data);
      setUploadStatus('success');

      // Convert to full ProtocolConfig and add to registry
      const parsed: ParsedProtocol = result.data;
      const config = convertParsedToConfig(parsed);
      addImportedProtocol(config);

      // Add to the import tab's protocol list
      const newProtocol: ImportedProtocol = {
        id: config.id,
        name: parsed.trial_name || parsed.protocol_id,
        sponsor: parsed.sponsor || 'Unknown',
        version: '1.0',
        status: 'Active',
        lastUpdated: new Date().toLocaleDateString(),
        data: parsed,
      };
      setImportedProtocols((prev) => [newProtocol, ...prev.filter((p) => p.id !== config.id)]);

      // Auto-select the new protocol and switch to budget tab
      setSelectedProtocolId(config.id);
      setSelectedSiteKey(config.sites[0]?.key ?? '');
      setNumCycles(config.defaultCycles);
      setRateOverrides({});

      // Show success banner
      setImportSuccess(config.trialName);
      setTimeout(() => setImportSuccess(null), 8000);

      // Auto-generate forecast from protocol data
      try {
        const perPatientCost = computeProtocolBudget(config, config.sites[0]?.key ?? '', config.defaultCycles, {});
        const trialTotal = computeTrialTotal(config, config.defaultCycles, {});
        const fRes = await fetch('/api/v1/forecasting/from-protocol', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            protocol_id: config.id,
            total_trial_budget: trialTotal,
            per_patient_cost: perPatientCost,
            n_patients: config.totalPatients,
            visit_costs: {},
            total_weeks: 104,
            enrolment_rate_per_week: 1.0,
            actual_spend_by_week: [],
            current_week: 0,
            patients_enrolled: 0,
          }),
        });
        if (fRes.ok) setForecastData(await fRes.json());
      } catch {
        // Forecast is optional — budget import still succeeded
      }
    } catch (err: any) {
      console.error('[d3ri-upload] Upload failed:', err);
      setUploadError(err.message || 'Failed to parse protocol PDF.');
      setUploadStatus('error');
    }
  };

  const handleDrop = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); if (!backendReady) { console.warn('[d3ri-upload] Drop ignored — backend not ready yet (backendReady=false). Health check may have failed.'); return; } const f = e.dataTransfer.files[0]; if (f) handleFileUpload(f); };
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => { if (!backendReady) { console.warn('[d3ri-upload] File input ignored — backend not ready yet (backendReady=false). Health check may have failed.'); return; } const f = e.target.files?.[0]; if (f) handleFileUpload(f); };

  // ---------------------------------------------------------------------------
  // Tab 0: Import Protocol
  // ---------------------------------------------------------------------------
  const renderImport = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Server warm-up banner — visible for 3 seconds on mount */}
      {warmingUp && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', backgroundColor: '#1a365d', borderRadius: 8, border: '1px solid #2b6cb0' }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: backendReady ? '#48bb78' : '#ecc94b', animation: backendReady ? 'none' : 'pulse 1.5s ease-in-out infinite' }} />
          <span style={{ fontSize: 13, color: '#e2e8f0' }}>
            {backendReady ? 'Server ready' : 'Connecting to server\u2026'}
          </span>
          {!backendReady && <span style={{ fontSize: 11, color: '#a0aec0' }}>Waking up backend \u2014 this takes a few seconds on first load</span>}
        </div>
      )}
      {/* Upload Area */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        style={{
          ...cardStyle,
          border: isDragging ? '2px dashed #4299e1' : '2px dashed #4a5568',
          backgroundColor: isDragging ? '#1a365d' : '#1a202c',
          textAlign: 'center',
          padding: '48px 32px',
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        onClick={() => document.getElementById('protocol-file-input')?.click()}
      >
        <input id="protocol-file-input" type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleFileInput} />
        <div style={{ fontSize: 48, marginBottom: 16, color: isDragging ? '#4299e1' : '#4a5568' }}>
          {!backendReady && uploadStatus === 'idle' ? '\u{1F504}' : uploadStatus === 'uploading' ? '\u23F3' : uploadStatus === 'success' ? '\u2705' : '\u{1F4C4}'}
        </div>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#e2e8f0', marginBottom: 8 }}>
          {!backendReady && uploadStatus === 'idle' ? 'Connecting to server...' : uploadStatus === 'uploading' ? 'Processing protocol PDF...' : uploadStatus === 'success' ? 'Protocol parsed successfully!' : 'Drag & drop protocol PDF here'}
        </div>
        <div style={{ fontSize: 13, color: '#a0aec0', marginBottom: 16 }}>
          {uploadStatus === 'idle' && !backendReady && 'Waking up backend — this takes a few seconds on first load'}
          {uploadStatus === 'idle' && backendReady && 'or click to browse files'}
          {uploadStatus === 'uploading' && `Extracting SOA grid... ${uploadProgress}%`}
          {uploadStatus === 'success' && `Confidence: ${((parsedResult?.confidence_score ?? 0) * 100).toFixed(0)}%`}
        </div>
        {!backendReady && uploadStatus === 'idle' && (
          <div style={{ maxWidth: 300, margin: '0 auto', backgroundColor: '#171923', borderRadius: 4, height: 6, overflow: 'hidden' }}>
            <div style={{ width: '60%', height: '100%', backgroundColor: '#4299e1', borderRadius: 4, animation: 'pulse 1.5s ease-in-out infinite' }} />
          </div>
        )}
        {uploadStatus === 'uploading' && (
          <div style={{ maxWidth: 300, margin: '0 auto', backgroundColor: '#171923', borderRadius: 4, height: 6, overflow: 'hidden' }}>
            <div style={{ width: `${uploadProgress}%`, height: '100%', backgroundColor: '#4299e1', borderRadius: 4, transition: 'width 0.3s' }} />
          </div>
        )}
        {uploadStatus === 'idle' && (
          <button
            disabled={!backendReady}
            style={{ padding: '10px 24px', borderRadius: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: backendReady ? 'pointer' : 'not-allowed', backgroundColor: backendReady ? '#2b6cb0' : '#4a5568', color: '#fff', marginTop: 8, opacity: backendReady ? 1 : 0.6 }}
            onClick={(e) => { e.stopPropagation(); if (backendReady) document.getElementById('protocol-file-input')?.click(); }}
          >
            {backendReady ? 'Upload Protocol PDF' : 'Waiting for server...'}
          </button>
        )}
        {uploadStatus === 'success' && (
          <button
            style={{ padding: '8px 20px', borderRadius: 6, fontSize: 13, fontWeight: 600, border: '1px solid #4a5568', cursor: 'pointer', backgroundColor: 'transparent', color: '#a0aec0', marginTop: 8 }}
            onClick={(e) => { e.stopPropagation(); setUploadStatus('idle'); setParsedResult(null); setUploadProgress(0); }}
          >
            Upload Another Protocol
          </button>
        )}
        <div style={{ fontSize: 11, color: '#718096', marginTop: 12 }}>Accepted: PDF only &middot; Max size: 50 MB</div>
      </div>

      {uploadStatus === 'error' && (
        <div style={{ ...cardStyle, border: '1px solid #742a2a' }}>
          <div style={{ color: '#f56565', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Upload Error</div>
          <div style={{ color: '#fc8181', fontSize: 12 }}>{uploadError}</div>
        </div>
      )}

      {/* Extraction capabilities */}
      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 12 }}>D3RI will automatically extract:</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          {['Schedule of Assessments grid', 'Visit schedule and windows', 'Procedures per visit', 'CPT code mapping', 'Per-patient budget estimate'].map((item) => (
            <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#48bb78', fontSize: 14 }}>{'\u2713'}</span>
              <span style={{ fontSize: 13, color: '#e2e8f0' }}>{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Parsed result preview */}
      {parsedResult && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0' }}>Extracted Protocol Data</div>
          <div style={cardStyle}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              {[
                { label: 'Protocol ID', value: parsedResult.protocol_id },
                { label: 'Trial Name', value: parsedResult.trial_name },
                { label: 'Sponsor', value: parsedResult.sponsor },
                { label: 'Phase', value: parsedResult.phase },
                { label: 'Visits Found', value: parsedResult.visits.length },
                { label: 'Procedures Found', value: parsedResult.procedures.length },
              ].map((item) => (
                <div key={item.label}>
                  <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</div>
                  <div style={{ fontSize: 14, color: '#e2e8f0', fontWeight: 600 }}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ ...cardStyle, flex: 1 }}>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Extraction Confidence</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: parsedResult.confidence_score >= 0.7 ? '#48bb78' : parsedResult.confidence_score >= 0.5 ? '#ecc94b' : '#f56565' }}>
                {(parsedResult.confidence_score * 100).toFixed(0)}%
              </div>
              <div style={{ marginTop: 8, backgroundColor: '#171923', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                <div style={{ width: `${parsedResult.confidence_score * 100}%`, height: '100%', backgroundColor: parsedResult.confidence_score >= 0.7 ? '#48bb78' : parsedResult.confidence_score >= 0.5 ? '#ecc94b' : '#f56565', borderRadius: 4 }} />
              </div>
            </div>
            {parsedResult.manual_review_needed.length > 0 && (
              <div style={{ ...cardStyle, flex: 1, border: '1px solid #744210' }}>
                <div style={{ fontSize: 11, color: '#ecc94b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Manual Review Needed ({parsedResult.manual_review_needed.length})</div>
                {parsedResult.manual_review_needed.slice(0, 5).map((item) => (
                  <div key={item} style={{ fontSize: 12, color: '#ecc94b' }}>{'\u26A0'} {item}</div>
                ))}
                {parsedResult.manual_review_needed.length > 5 && <div style={{ fontSize: 11, color: '#a0aec0' }}>+{parsedResult.manual_review_needed.length - 5} more...</div>}
              </div>
            )}
          </div>

          {parsedResult.visits.length > 0 && parsedResult.procedures.length > 0 && (
            <div style={{ ...cardStyle, overflowX: 'auto' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 12 }}>Extracted SOA Grid Preview</div>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 600 }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, position: 'sticky', left: 0, backgroundColor: '#1a202c', zIndex: 2, minWidth: 180 }}>Procedure</th>
                    {parsedResult.visits.slice(0, 12).map((v) => (
                      <th key={v} style={{ ...thStyle, textAlign: 'center', minWidth: 50, fontSize: 10 }}>{v}</th>
                    ))}
                    {parsedResult.visits.length > 12 && <th style={{ ...thStyle, textAlign: 'center', fontSize: 10 }}>+{parsedResult.visits.length - 12}</th>}
                  </tr>
                </thead>
                <tbody>
                  {parsedResult.procedures.slice(0, 15).map((proc) => (
                    <tr key={proc}>
                      <td style={{ ...tdStyle, position: 'sticky', left: 0, backgroundColor: '#1a202c', fontWeight: 600, zIndex: 1, fontSize: 12 }}>{proc}</td>
                      {parsedResult.visits.slice(0, 12).map((v) => (
                        <td key={v} style={{ ...tdStyle, textAlign: 'center' }}>
                          {parsedResult.soa_grid[proc]?.[v] ? <span style={{ color: '#63b3ed', fontSize: 14 }}>{'\u25CF'}</span> : <span style={{ color: '#4a5568', fontSize: 14 }}>{'\u25CB'}</span>}
                        </td>
                      ))}
                      {parsedResult.visits.length > 12 && <td style={tdStyle} />}
                    </tr>
                  ))}
                  {parsedResult.procedures.length > 15 && (
                    <tr><td style={{ ...tdStyle, color: '#a0aec0', fontStyle: 'italic' }} colSpan={Math.min(parsedResult.visits.length, 12) + 2}>+{parsedResult.procedures.length - 15} more procedures...</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <button style={{ padding: '10px 24px', borderRadius: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer', backgroundColor: '#2b6cb0', color: '#fff' }} onClick={() => setActiveTab('soa')}>View Full SOA Grid</button>
            <button style={{ padding: '10px 24px', borderRadius: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer', backgroundColor: '#276749', color: '#fff' }} onClick={() => setActiveTab('cpt')}>View CPT Mapping</button>
            <button style={{ padding: '10px 24px', borderRadius: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer', backgroundColor: '#744210', color: '#fff' }} onClick={() => setActiveTab('perpatient')}>Calculate Budget</button>
            {forecastData && (
              <button style={{ padding: '10px 24px', borderRadius: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer', backgroundColor: '#553c9a', color: '#fff' }} onClick={() => setActiveTab('forecast')}>
                View Forecast {'\u2192'}
              </button>
            )}
          </div>

          {/* Import success summary */}
          {forecastData && (
            <div style={{ ...cardStyle, border: '1px solid #48bb78', marginTop: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#48bb78', marginBottom: 10 }}>Protocol imported successfully</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 13, color: '#e2e8f0' }}>
                  <strong>SOA:</strong> {parsedResult.procedures.length} procedures &middot; {parsedResult.visits.length} visits
                </div>
                <div style={{ fontSize: 13, color: '#e2e8f0' }}>
                  <strong>Budget:</strong> {fmt(Math.round(forecastData.eac))} total &middot; {fmt(Math.round(forecastData.eac / 50))}/patient
                </div>
                <div style={{ fontSize: 13, color: '#e2e8f0' }}>
                  <strong>Forecast:</strong> {forecastData.budget_status} &middot; {(forecastData.overrun_probability * 100).toFixed(0)}% overrun risk baseline
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Previously imported protocols */}
      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 16 }}>Previously imported protocols:</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {importedProtocols.map((p) => (
            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', backgroundColor: '#171923', borderRadius: 6, border: '1px solid #2d3748' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0' }}>
                  {p.name} &mdash; {p.sponsor}
                  <span style={{ ...badgeStyle(p.status === 'Active' ? 'green' : p.status === 'Draft' ? 'amber' : 'blue'), marginLeft: 8 }}>{p.status}</span>
                </div>
                <div style={{ fontSize: 11, color: '#718096', marginTop: 2 }}>Last updated: {p.lastUpdated}</div>
              </div>
              <button
                style={{ padding: '6px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid #2d3748', cursor: 'pointer', backgroundColor: 'transparent', color: '#63b3ed' }}
                onClick={() => { const cfg = allProtocols[p.id]; if (cfg) { setSelectedProtocolId(p.id); setSelectedSiteKey(cfg.sites[0]?.key ?? ''); setNumCycles(cfg.defaultCycles); } setActiveTab('perpatient'); }}
              >
                View Budget
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Tab 1: SOA Import (dynamic)
  // ---------------------------------------------------------------------------
  const renderSOA = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={cardStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {[
            { label: 'Trial ID', value: protocol.trialName },
            { label: 'Protocol', value: protocol.protocolTitle },
            { label: 'Sponsor', value: protocol.sponsor },
            { label: 'Phase', value: protocol.phase },
            { label: 'Version', value: `${protocol.version} / ${protocol.lastUpdated}` },
            { label: 'Patients', value: protocol.patientLabel },
          ].map((item) => (
            <div key={item.label}>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</div>
              <div style={{ fontSize: 14, color: '#e2e8f0', fontWeight: 600 }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...cardStyle, overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, position: 'sticky', left: 0, backgroundColor: '#1a202c', zIndex: 2, minWidth: 180 }}>Procedure</th>
              {protocol.visits.map((v) => (
                <th key={v} style={{ ...thStyle, textAlign: 'center', minWidth: 60, fontSize: protocol.visits.length > 8 ? 9 : 11 }}>{v}</th>
              ))}
              <th style={{ ...thStyle, textAlign: 'center' }}>Window</th>
            </tr>
          </thead>
          <tbody>
            {protocol.procedures.map((proc) => (
              <tr key={proc.name}>
                <td style={{ ...tdStyle, position: 'sticky', left: 0, backgroundColor: '#1a202c', fontWeight: 600, zIndex: 1 }}>{proc.name}</td>
                {protocol.visits.map((v) => (
                  <td key={v} style={{ ...tdStyle, textAlign: 'center' }}>
                    {proc.visits.includes(v)
                      ? <span style={{ color: '#63b3ed', fontSize: 16 }}>{'\u25CF'}</span>
                      : <span style={{ color: '#4a5568', fontSize: 16 }}>{'\u25CB'}</span>}
                  </td>
                ))}
                <td style={{ ...tdStyle, textAlign: 'center', fontSize: 11, color: '#a0aec0' }}>{proc.window}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 11, color: '#718096', fontStyle: 'italic' }}>
        SOA grid for {protocol.trialName} Protocol v{protocol.version}
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Tab 2: CPT Code Mapping (dynamic)
  // ---------------------------------------------------------------------------
  const handleRateEdit = (procedure: string, site: string, value: string) => {
    const num = parseFloat(value);
    if (!isNaN(num) && num >= 0) {
      setRateOverrides((prev) => ({
        ...prev,
        [procedure]: { ...(prev[procedure] || {}), [site]: num },
      }));
    }
  };

  const renderCPT = () => {
    const siteKeys = protocol.sites.map((s) => s.key);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        <div style={{ ...cardStyle, overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={thStyle}>Procedure</th>
                <th style={thStyle}>CPT</th>
                <th style={thStyle}>Description</th>
                {siteKeys.map((s) => (
                  <th key={s} style={{ ...thStyle, textAlign: 'right' }}>{s}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {protocol.cptMappings.map((m) => (
                <tr key={m.procedure + m.cpt}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{m.procedure}</td>
                  <td style={tdStyle}><span style={badgeStyle('blue')}>{m.cpt}</span></td>
                  <td style={{ ...tdStyle, color: '#a0aec0', fontSize: 12 }}>{m.description}</td>
                  {siteKeys.map((site) => {
                    const cellKey = `${m.procedure}-${site}`;
                    const rate = getCPTRate(m.procedure, site, protocol.cptMappings, rateOverrides);
                    return (
                      <td key={site} style={{ ...tdStyle, textAlign: 'right', cursor: 'pointer' }}>
                        {editingCell === cellKey ? (
                          <input
                            type="number"
                            defaultValue={rate}
                            autoFocus
                            onBlur={(e) => { handleRateEdit(m.procedure, site, e.target.value); setEditingCell(null); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { handleRateEdit(m.procedure, site, (e.target as HTMLInputElement).value); setEditingCell(null); } }}
                            style={{ width: 80, backgroundColor: '#2d3748', border: '1px solid #4a5568', borderRadius: 4, color: '#e2e8f0', fontSize: 13, padding: '2px 6px', textAlign: 'right' }}
                          />
                        ) : (
                          <span onClick={() => setEditingCell(cellKey)}>{fmt(rate)}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11, color: '#718096', fontStyle: 'italic' }}>
          Rates based on 2026 Medicare fee schedule + institutional markups. Click any rate to edit.
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Tab 3: Per-Patient Budget (dynamic)
  // ---------------------------------------------------------------------------
  const renderPerPatient = () => {
    const siteKeys = protocol.sites.map((s) => s.key);
    const siteLabels: Record<string, string> = {};
    for (const s of protocol.sites) siteLabels[s.key] = s.label;
    siteLabels['ALL'] = 'All Sites';

    const chartData = [...visitBreakdown]
      .filter((v) => v.totalCost > 0)
      .sort((a, b) => b.totalCost - a.totalCost)
      .map((v) => ({ name: v.visit, cost: v.totalCost }));

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>


        {/* Selectors */}
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          {protocol.sites.length > 1 && (
            <div>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Site</div>
              <div style={{ display: 'flex', gap: 4 }}>
                {[...siteKeys, 'ALL'].map((s) => (
                  <button key={s} style={tabBtnStyle(selectedSiteKey === s)} onClick={() => setSelectedSiteKey(s)}>
                    {siteLabels[s] || s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {protocol.cycleOptions.length > 1 && (
            <div>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Cycles</div>
              <div style={{ display: 'flex', gap: 4 }}>
                {protocol.cycleOptions.map((c) => (
                  <button key={c} style={tabBtnStyle(numCycles === c)} onClick={() => setNumCycles(c)}>{c}</button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Visit cost cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {visitBreakdown.map((v) => (
            <div key={v.visit} style={cardStyle}>
              <div style={{ fontSize: 12, color: '#a0aec0', fontWeight: 600, marginBottom: 4 }}>{v.visit}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>{fmt(v.totalCost)}</span>
                {v.mult > 1 && <span style={badgeStyle('blue')}>{v.mult}x</span>}
              </div>
              <div style={{ fontSize: 11, color: '#718096', marginTop: 4 }}>{v.procCount} procedures{v.mult > 1 ? ` \u00d7 ${v.mult} cycles` : ''}</div>
              <div style={{ fontSize: 11, color: '#a0aec0', marginTop: 2 }}>Cumulative: {fmt(v.cumulative)}</div>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div style={cardStyle}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase' }}>Treatment Subtotal</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>{fmt(treatmentSubtotal)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase' }}>Site Setup</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>{fmt(protocol.siteSetup)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase' }}>Overhead ({(protocol.overheadRate * 100).toFixed(0)}%)</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>{fmt(overheadAmount)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase' }}>Total Per Patient</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#48bb78' }}>{fmt(totalPerPatient)}</div>
            </div>
          </div>
        </div>

        {/* Bar chart */}
        <div style={cardStyle}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 12 }}>Cost by Visit Type</div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 20, bottom: 60 }}>
              <XAxis dataKey="name" tick={{ fill: '#a0aec0', fontSize: 10 }} angle={-45} textAnchor="end" />
              <YAxis tick={{ fill: '#a0aec0', fontSize: 11 }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ backgroundColor: '#1a202c', border: '1px solid #2d3748', borderRadius: 6 }} labelStyle={{ color: '#e2e8f0' }} formatter={(value: any) => [fmt(Number(value)), 'Cost']} />
              <Bar dataKey="cost" radius={[4, 4, 0, 0]}>
                {chartData.map((_, i) => <Cell key={i} fill={i === 0 ? '#4299e1' : '#2b6cb0'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Site comparison (only if multiple sites) */}
        {protocol.sites.length > 1 && (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 4 }}>Site Comparison — Per-Patient Total</div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${protocol.sites.length}, 1fr)`, gap: 12 }}>
              {protocol.sites.map((s) => (
                <div key={s.key} style={cardStyle}>
                  <div style={{ fontSize: 12, color: '#a0aec0', fontWeight: 600, marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#e2e8f0' }}>{fmt(perPatientCost[s.key] ?? 0)}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Tab 4: Trial Budget Summary (dynamic)
  // ---------------------------------------------------------------------------
  const renderSummary = () => {
    const siteData = protocol.sites.map((s) => {
      const ppc = perPatientCost[s.key] ?? 0;
      const patientCosts = ppc * s.patients;
      const monitoringVisits = numCycles * protocol.monitoringVisitsPerCycle;
      const monitoring = monitoringVisits * protocol.monitoringCostPerVisit;
      const dataManagement = s.patients * protocol.dataManagementPerPatient;
      const siteTotal = patientCosts + protocol.siteSetup + monitoring + dataManagement;
      return { site: s.key, label: s.label, pts: s.patients, patientCosts, monitoring, monitoringVisits, dataManagement, siteTotal };
    });

    const trialTotal = siteData.reduce((s, d) => s + d.siteTotal, 0);
    const totalPatientCosts = siteData.reduce((s, d) => s + d.patientCosts, 0);
    const totalSiteSetup = protocol.sites.length * protocol.siteSetup;
    const totalMonitoring = siteData.reduce((s, d) => s + d.monitoring, 0);
    const totalDataMgmt = siteData.reduce((s, d) => s + d.dataManagement, 0);

    const pieData = [
      { name: 'Per-Patient Costs', value: totalPatientCosts },
      { name: 'Site Setup', value: totalSiteSetup },
      { name: 'Monitoring', value: totalMonitoring },
      { name: 'Data Management', value: totalDataMgmt },
    ];

    const actualToDate = protocol.id === 'sym004-09' ? 847200 : protocol.id === 'herald' ? 42500000 : 0;
    const actualPct = trialTotal > 0 ? (actualToDate / trialTotal) * 100 : 0;
    const projectedFinal = Math.round(trialTotal * 1.011);
    const variancePct = trialTotal > 0 ? Math.abs(((projectedFinal - trialTotal) / trialTotal) * 100) : 0;
    const varianceStatus: 'green' | 'amber' | 'red' = variancePct <= 5 ? 'green' : variancePct <= 15 ? 'amber' : 'red';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>


        <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>
          {protocol.sites.length > 1
            ? `Site Allocation: ${protocol.sites.map((s) => `${s.label} ${s.patients.toLocaleString()} pts`).join(', ')}`
            : `Total enrollment: ${protocol.totalPatients.toLocaleString()} patients`}
        </div>

        {/* Site cards */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(siteData.length, 3)}, 1fr)`, gap: 12 }}>
          {siteData.map((d) => (
            <div key={d.site} style={cardStyle}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0', marginBottom: 12 }}>{d.label}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                  <span style={{ color: '#a0aec0' }}>Per-patient x {d.pts.toLocaleString()}</span>
                  <span style={{ color: '#e2e8f0' }}>{fmt(d.patientCosts)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                  <span style={{ color: '#a0aec0' }}>Site setup</span>
                  <span style={{ color: '#e2e8f0' }}>{fmt(protocol.siteSetup)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                  <span style={{ color: '#a0aec0' }}>Monitoring ({d.monitoringVisits} visits x {fmt(protocol.monitoringCostPerVisit)})</span>
                  <span style={{ color: '#e2e8f0' }}>{fmt(d.monitoring)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                  <span style={{ color: '#a0aec0' }}>Data management</span>
                  <span style={{ color: '#e2e8f0' }}>{fmt(d.dataManagement)}</span>
                </div>
                <div style={{ borderTop: '1px solid #2d3748', paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#a0aec0' }}>Site Total</span>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#48bb78' }}>{fmt(d.siteTotal)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Trial total */}
        <div style={{ ...cardStyle, textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Trial Total</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#48bb78' }}>{fmt(trialTotal)}</div>
        </div>

        {/* Pie chart */}
        <div style={cardStyle}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 12 }}>Budget Breakdown</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <ResponsiveContainer width="50%" height={250}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={50} strokeWidth={0}>
                  {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: '#1a202c', border: '1px solid #2d3748', borderRadius: 6 }} formatter={(value: any) => [fmt(Number(value)), '']} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {pieData.map((d, i) => (
                <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: PIE_COLORS[i] }} />
                  <span style={{ fontSize: 12, color: '#a0aec0' }}>{d.name}</span>
                  <span style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 600 }}>{fmt(d.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Budget vs Actual */}
        <div style={cardStyle}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 16 }}>Budget vs Actual Tracker</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase' }}>Budget</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>{fmt(trialTotal)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase' }}>Actual to Date</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>{fmt(actualToDate)}</div>
              <div style={{ fontSize: 11, color: '#a0aec0' }}>{actualPct.toFixed(1)}%</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase' }}>Variance</div>
              <div><span style={badgeStyle(varianceStatus)}>{varianceStatus === 'green' ? 'On Track' : varianceStatus === 'amber' ? 'Caution' : 'Over Budget'}</span></div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase' }}>Projected Final</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>{fmt(projectedFinal)}</div>
            </div>
          </div>
          <div style={{ marginTop: 16, backgroundColor: '#171923', borderRadius: 4, height: 8, overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(actualPct, 100)}%`, height: '100%', backgroundColor: varianceStatus === 'green' ? '#48bb78' : varianceStatus === 'amber' ? '#ecc94b' : '#f56565', borderRadius: 4, transition: 'width 0.3s' }} />
          </div>
          <div style={{ fontSize: 11, color: '#718096', marginTop: 8, fontStyle: 'italic' }}>Variance alerts: green if within 5%, amber 5-15%, red &gt;15%.</div>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Tab 5: Protocol Comparison
  // ---------------------------------------------------------------------------
  const renderCompare = () => {
    const protocols = Object.values(allProtocols);
    const compData = protocols.map((p) => {
      const siteKey = p.sites[0]?.key ?? '';
      const perPatient = computeProtocolBudget(p, siteKey, p.defaultCycles, {});
      const trialTotal = computeTrialTotal(p, p.defaultCycles, {});
      return { protocol: p, perPatient, trialTotal };
    });

    const maxPerPatient = Math.max(...compData.map((d) => d.perPatient));

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>Protocol Comparison</div>

        {/* Comparison table */}
        <div style={{ ...cardStyle, overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, minWidth: 160 }}></th>
                {compData.map((d) => (
                  <th key={d.protocol.id} style={{ ...thStyle, textAlign: 'right', minWidth: 180 }}>{d.protocol.trialName}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Sponsor', values: compData.map((d) => d.protocol.sponsor) },
                { label: 'Phase', values: compData.map((d) => d.protocol.phase) },
                { label: 'Indication', values: compData.map((d) => indicationMap[d.protocol.id] || 'Clinical Trial') },
                { label: 'Total Patients', values: compData.map((d) => d.protocol.totalPatients.toLocaleString()) },
                { label: 'Visits per Patient', values: compData.map((d) => d.protocol.visits.length.toString()) },
                { label: 'Unique Procedures', values: compData.map((d) => d.protocol.procedures.length.toString()) },
                { label: 'Sites', values: compData.map((d) => d.protocol.id === 'herald' ? '~200' : d.protocol.sites.length.toString()) },
                { label: 'Per-Patient Cost', values: compData.map((d) => fmt(Math.round(d.perPatient))), highlight: true },
                { label: 'Trial Total', values: compData.map((d) => fmt(Math.round(d.trialTotal))), highlight: true },
                { label: 'Overhead Rate', values: compData.map((d) => `${(d.protocol.overheadRate * 100).toFixed(0)}%`) },
                { label: 'Site Setup', values: compData.map((d) => fmt(d.protocol.siteSetup)) },
              ].map((row) => (
                <tr key={row.label}>
                  <td style={{ ...tdStyle, fontWeight: 600, color: '#a0aec0' }}>{row.label}</td>
                  {row.values.map((v, i) => (
                    <td key={i} style={{ ...tdStyle, textAlign: 'right', fontWeight: (row as any).highlight ? 700 : 400, color: (row as any).highlight ? '#48bb78' : '#e2e8f0', fontSize: (row as any).highlight ? 15 : 13 }}>{v}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Per-patient cost bar comparison */}
        <div style={cardStyle}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 16 }}>Per-Patient Cost Comparison</div>
          {compData.map((d, i) => {
            const pct = maxPerPatient > 0 ? (d.perPatient / maxPerPatient) * 100 : 0;
            return (
              <div key={d.protocol.id} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600 }}>{d.protocol.trialName}</span>
                  <span style={{ fontSize: 13, color: '#48bb78', fontWeight: 700 }}>{fmt(Math.round(d.perPatient))}</span>
                </div>
                <div style={{ backgroundColor: '#171923', borderRadius: 4, height: 24, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', backgroundColor: PIE_COLORS[i % PIE_COLORS.length], borderRadius: 4, transition: 'width 0.3s', minWidth: pct > 0 ? 4 : 0 }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Key insight */}
        <div style={{ ...cardStyle, border: '1px solid #2b6cb0' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#63b3ed', marginBottom: 8 }}>Key Difference</div>
          <div style={{ fontSize: 13, color: '#e2e8f0', lineHeight: 1.6 }}>
            {compData.length >= 2 && (() => {
              const sorted = [...compData].sort((a, b) => b.perPatient - a.perPatient);
              const ratio = sorted[0].perPatient / Math.max(sorted[sorted.length - 1].perPatient, 1);
              return (
                <>
                  <strong>{sorted[0].protocol.trialName}</strong> costs <strong>{ratio.toFixed(0)}x more per patient</strong> than{' '}
                  <strong>{sorted[sorted.length - 1].protocol.trialName}</strong>.
                  {sorted[0].protocol.id === 'sym004-09' && (
                    <span style={{ color: '#a0aec0' }}>
                      {' '}Oncology trials are significantly more expensive per patient than vaccine trials due to CT scans, chemotherapy infusions, PK sampling, tumor biopsies, and intensive monitoring requirements.
                    </span>
                  )}
                  <br /><br />
                  However, <strong>{sorted[sorted.length - 1].protocol.trialName}</strong> has a higher <strong>trial total</strong> ({fmt(Math.round(sorted.find((d) => d.trialTotal === Math.max(...compData.map((c) => c.trialTotal)))!.trialTotal))}) due to significantly larger patient enrollment ({sorted.find((d) => d.trialTotal === Math.max(...compData.map((c) => c.trialTotal)))!.protocol.totalPatients.toLocaleString()} patients).
                </>
              );
            })()}
          </div>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Tab 6: Forecast (M19 Cost Overrun)
  // ---------------------------------------------------------------------------
  interface ForecastData {
    trial_id: string; current_week: number;
    bcwp: number; acwp: number; bcws: number;
    cpi: number; spi: number; cv: number; sv: number;
    eac: number; etc: number; vac: number;
    overrun_amount: number; overrun_probability: number; weeks_to_breach: number;
    budget_status: string; cpi_trend: string; recommendation: string;
    pct_complete: number; progress_basis: string;
    forecast_basis?: string; planned_spend_by_week?: number[];
  }

  // Per-protocol demo forecast data
  const PROTOCOL_FORECAST_DATA: Record<string, { budget_total: number; patients_planned: number; current_week: number; total_weeks: number; patients_enrolled: number; actual_spend_by_week: number[] }> = {
    'sym004-09': {
      budget_total: 4079290, patients_planned: 50, current_week: 12, total_weeks: 104, patients_enrolled: 9,
      actual_spend_by_week: [120000, 198000, 287000, 362000, 441000, 512000, 589000, 641000, 698000, 742000, 798000, 847200],
    },
    'herald': {
      budget_total: 198961500, patients_planned: 35300, current_week: 8, total_weeks: 156, patients_enrolled: 850,
      actual_spend_by_week: [4200000, 8100000, 13400000, 19100000, 24800000, 31200000, 37600000, 42900000],
    },
  };

  const getForecastPayload = useCallback(() => {
    const known = PROTOCOL_FORECAST_DATA[selectedProtocolId];
    if (known) {
      return { trial_id: protocol.trialName, ...known };
    }
    // Imported protocol — use pre-trial baseline via /from-protocol
    const trialTotal = computeTrialTotal(protocol, protocol.defaultCycles, rateOverrides);
    const ppc = computeProtocolBudget(protocol, protocol.sites[0]?.key ?? '', protocol.defaultCycles, rateOverrides);
    return {
      trial_id: protocol.trialName,
      budget_total: trialTotal,
      patients_planned: protocol.totalPatients,
      current_week: 0,
      total_weeks: 104,
      patients_enrolled: 0,
      actual_spend_by_week: [] as number[],
      per_patient_cost: ppc,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProtocolId, protocol, rateOverrides]);

  // Generate S-curve for current protocol
  const forecastPlanned = useMemo(() => {
    const payload = getForecastPayload();
    const BAC = payload.budget_total;
    const W = payload.total_weeks;
    const raw = Array.from({ length: W }, (_, i) => 1.0 / (1.0 + Math.exp(-0.08 * ((i + 1) - W * 0.38))));
    const scale = raw[raw.length - 1];
    return raw.map((v) => (v / scale) * BAC);
  }, [getForecastPayload]);

  const [forecastData, setForecastData] = useState<ForecastData | null>(null);
  const [forecastLoading, setForecastLoading] = useState(false);

  const fetchForecast = useCallback(async () => {
    setForecastLoading(true);
    const payload = getForecastPayload();
    try {
      const API_BASE = process.env.REACT_APP_API_URL || '';
      // Use /from-protocol for pre-trial, /cost-overrun for in-progress
      const isPreTrial = payload.current_week === 0 || payload.actual_spend_by_week.length === 0;
      const endpoint = isPreTrial ? '/api/v1/forecasting/from-protocol' : '/api/v1/forecasting/cost-overrun';
      const body = isPreTrial
        ? { protocol_id: payload.trial_id, total_trial_budget: payload.budget_total, per_patient_cost: (payload as any).per_patient_cost || payload.budget_total / payload.patients_planned, n_patients: payload.patients_planned, visit_costs: {}, total_weeks: payload.total_weeks, enrolment_rate_per_week: 1.0, actual_spend_by_week: [], current_week: 0, patients_enrolled: 0 }
        : { ...payload, planned_spend_by_week: forecastPlanned };
      const res = await fetch(`${API_BASE}${endpoint}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error(`API ${res.status}`);
      setForecastData(await res.json());
    } catch {
      // Fallback: compute locally
      const BAC = payload.budget_total;
      if (payload.current_week === 0 || payload.actual_spend_by_week.length === 0) {
        setForecastData({
          trial_id: payload.trial_id, current_week: 0,
          bcwp: 0, acwp: 0, bcws: 0, cpi: 1.0, spi: 1.0, cv: 0, sv: 0,
          eac: BAC, etc: BAC, vac: 0, overrun_amount: 0, overrun_probability: 0.15,
          weeks_to_breach: -1, budget_status: 'Pre-trial baseline', cpi_trend: 'Not started',
          recommendation: `Protocol imported. Budget: $${BAC.toLocaleString()} over ${payload.total_weeks} weeks for ${payload.patients_planned} patients. Baseline overrun risk: 15%.`,
          pct_complete: 0, progress_basis: 'pre-trial', forecast_basis: 'pre-trial', planned_spend_by_week: forecastPlanned,
        });
      } else {
        const n = payload.current_week;
        const ACWP = payload.actual_spend_by_week[n - 1];
        const pctComplete = payload.patients_enrolled / payload.patients_planned;
        const BCWP = pctComplete * BAC;
        const BCWS = forecastPlanned[n - 1] || 0;
        const cpi = ACWP > 0 ? BCWP / ACWP : 1.0;
        const spi = BCWS > 0 ? BCWP / BCWS : 1.0;
        const eac = cpi > 0 ? BAC / cpi : BAC * 2;
        const vac = BAC - eac;
        const weeklyBurn = ACWP / n;
        const projFinal = ACWP + weeklyBurn * (payload.total_weeks - n);
        const wtb = projFinal > BAC && weeklyBurn > 0 ? Math.floor((BAC - ACWP) / weeklyBurn) : -1;
        const overrunAmt = eac - BAC;
        const overrunProb = Math.max(0, Math.min(1, (1 - cpi) * 2));
        const status = cpi >= 0.95 && overrunProb < 0.25 ? 'On Track' : cpi >= 0.90 && overrunProb < 0.50 ? 'Watch' : cpi >= 0.80 ? 'At Risk' : 'Critical';
        const rec = status === 'On Track' ? 'Budget on track. Continue standard monitoring.' :
          status === 'Watch' ? `CPI ${cpi.toFixed(2)} — minor cost pressure. Review site spend.` :
          status === 'At Risk' ? `CPI ${cpi.toFixed(2)} — projected overrun $${Math.round(overrunAmt).toLocaleString()}.` :
          `CRITICAL: CPI ${cpi.toFixed(2)}. EAC $${Math.round(eac).toLocaleString()} exceeds budget.`;
        setForecastData({
          trial_id: payload.trial_id, current_week: n,
          bcwp: Math.round(BCWP), acwp: ACWP, bcws: Math.round(BCWS),
          cpi: parseFloat(cpi.toFixed(4)), spi: parseFloat(spi.toFixed(4)),
          cv: Math.round(BCWP - ACWP), sv: Math.round(BCWP - BCWS),
          eac: Math.round(eac), etc: Math.round((BAC - BCWP) / cpi), vac: Math.round(vac),
          overrun_amount: Math.round(overrunAmt), overrun_probability: parseFloat(overrunProb.toFixed(4)),
          weeks_to_breach: wtb, budget_status: status, cpi_trend: 'Stable', recommendation: rec,
          pct_complete: pctComplete, progress_basis: 'enrollment',
        });
      }
    } finally {
      setForecastLoading(false);
    }
  }, [getForecastPayload, forecastPlanned]);

  // Re-fetch forecast when protocol changes or tab is opened
  const [lastForecastProtocol, setLastForecastProtocol] = useState<string>('');
  useEffect(() => {
    if (activeTab === 'forecast' && selectedProtocolId !== lastForecastProtocol) {
      setLastForecastProtocol(selectedProtocolId);
      fetchForecast();
    }
  }, [activeTab, selectedProtocolId, lastForecastProtocol, fetchForecast]);

  const evmColor = (val: number, threshold1: number = 0.95, threshold2: number = 0.85) =>
    val >= threshold1 ? '#48bb78' : val >= threshold2 ? '#ecc94b' : '#f56565';

  const statusConfig: Record<string, { bg: string; border: string; color: string; icon: string }> = {
    'On Track': { bg: '#22543d', border: '#48bb78', color: '#48bb78', icon: '\u2713' },
    'Watch': { bg: '#744210', border: '#ecc94b', color: '#ecc94b', icon: '\u26A0' },
    'At Risk': { bg: '#7b341e', border: '#ed8936', color: '#ed8936', icon: '\u26A0' },
    'Critical': { bg: '#742a2a', border: '#f56565', color: '#f56565', icon: '\u2715' },
    'Pre-trial baseline': { bg: '#1a365d', border: '#4299e1', color: '#4299e1', icon: '\u2139' },
  };

  const renderForecast = () => {
    if (forecastLoading) return <div style={{ color: '#a0aec0', padding: 40, textAlign: 'center' }}>Loading forecast...</div>;
    if (!forecastData) return <div style={{ color: '#a0aec0', padding: 40, textAlign: 'center' }}>Select the Forecast tab to load forecast data.</div>;

    const d = forecastData;
    const sc = statusConfig[d.budget_status] || statusConfig['Watch'];

    // Pre-trial baseline view
    if (d.forecast_basis === 'pre-trial' || d.current_week === 0) {
      const preTrialPlanned = d.planned_spend_by_week || forecastPlanned;
      const preTrajectory = preTrialPlanned.map((v, i) => ({ week: i + 1, planned: Math.round(v) }));
      const preBudgetTotal = d.eac || computeTrialTotal(protocol, protocol.defaultCycles, rateOverrides);
      const siteData = protocol.sites.map((s) => ({
        label: s.label,
        budget: Math.round(preBudgetTotal * (s.patients / protocol.totalPatients)),
      }));
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Baseline summary card */}
          <div style={{ ...cardStyle, border: '1px solid #4299e1' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#4299e1', marginBottom: 12 }}>Pre-trial Baseline Forecast</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
              {[
                { label: 'Total Budget', value: fmt(Math.round(preBudgetTotal)) },
                { label: 'Per-Patient Cost', value: fmt(Math.round(preBudgetTotal / protocol.totalPatients)) },
                { label: 'Planned Patients', value: protocol.totalPatients.toLocaleString() },
                { label: 'Trial Duration', value: `${getForecastPayload().total_weeks} weeks` },
                { label: 'Baseline Risk', value: `${(d.overrun_probability * 100).toFixed(0)}%` },
              ].map((item) => (
                <div key={item.label}>
                  <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Pre-trial info banner */}
          <div style={{ backgroundColor: '#1a365d', border: '1px solid #2b6cb0', borderRadius: 8, padding: '12px 20px' }}>
            <span style={{ color: '#63b3ed', fontSize: 13 }}>
              {'\u2139'} This is a pre-trial projection. Import actual spend data each week to activate live forecasting.
            </span>
          </div>

          {/* Planned spend curve only */}
          <div style={cardStyle}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 4 }}>Planned Spend Curve</div>
            <div style={{ fontSize: 11, color: '#718096', marginBottom: 12 }}>Actual data will populate as the trial progresses</div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={preTrajectory} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2d3748" />
                <XAxis dataKey="week" tick={{ fill: '#a0aec0', fontSize: 10 }} label={{ value: 'Week', position: 'insideBottom', offset: -5, fill: '#a0aec0' }} />
                <YAxis tick={{ fill: '#a0aec0', fontSize: 10 }} tickFormatter={(v: number) => `$${(v / 1e6).toFixed(1)}M`} />
                <Tooltip contentStyle={{ backgroundColor: '#1a202c', border: '1px solid #2d3748', borderRadius: 6 }} formatter={(v: any) => [fmt(Number(v)), 'Planned']} labelFormatter={(l) => `Week ${l}`} />
                <ReferenceLine y={d.eac} stroke="#f56565" strokeDasharray="5 5" label={{ value: 'Budget Cap', fill: '#f56565', fontSize: 10, position: 'right' }} />
                <Line type="monotone" dataKey="planned" stroke="#4299e1" dot={false} name="Planned (BCWS)" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Site breakdown — planned only */}
          <div style={cardStyle}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 12 }}>Site Budget Allocation</div>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  {['Site', 'Budget', 'Actual', 'CPI', 'Status'].map((h) => (
                    <th key={h} style={{ ...thStyle, textAlign: h === 'Site' ? 'left' : 'right' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {siteData.map((s) => (
                  <tr key={s.label}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{s.label}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(s.budget)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#718096' }}>&mdash;</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#718096' }}>&mdash;</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}><span style={badgeStyle('blue')}>Pre-trial</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Recommendation */}
          <div style={{ ...cardStyle, borderLeft: '3px solid #4299e1' }}>
            <div style={{ fontSize: 13, color: '#e2e8f0' }}>{d.recommendation}</div>
          </div>
        </div>
      );
    }

    // Get actual spend from payload for this protocol
    const payload = getForecastPayload();
    const actualSpend = payload.actual_spend_by_week;
    const budgetTotal = payload.budget_total;
    const totalWeeks = payload.total_weeks;
    const curWeek = d.current_week;
    const patientsPlanned = payload.patients_planned;
    const patientsEnrolled = payload.patients_enrolled;
    const planned = d.planned_spend_by_week || forecastPlanned;

    // CPI history for sparkline
    const cpiHistory = actualSpend.slice(0, curWeek).map((_, i) => {
      const acwp_i = actualSpend[i];
      const enrolled_i = d.progress_basis === 'enrollment' && patientsPlanned > 0 ? patientsEnrolled * ((i + 1) / curWeek) : 0;
      const pct_i = d.progress_basis === 'enrollment' ? enrolled_i / patientsPlanned : (planned[i] || 0) / budgetTotal;
      const bcwp_i = pct_i * budgetTotal;
      const cpi_i = acwp_i > 0 ? bcwp_i / acwp_i : 1.0;
      return { week: i + 1, cpi: parseFloat(cpi_i.toFixed(3)) };
    });

    // Spend trajectory chart data
    const weeklyBurn = d.acwp / Math.max(curWeek, 1);
    const trajectoryData = Array.from({ length: totalWeeks }, (_, i) => {
      const w = i + 1;
      const entry: any = { week: w, planned: Math.round(planned[i] || 0) };
      if (w <= curWeek && i < actualSpend.length) entry.actual = actualSpend[i];
      if (w >= curWeek) entry.forecast = Math.round(d.acwp + weeklyBurn * (w - curWeek));
      return entry;
    });

    // Site breakdown data — derive from protocol config
    const siteData = protocol.sites.map((s) => {
      const siteBudget = Math.round(budgetTotal * (s.patients / patientsPlanned));
      const siteActual = Math.round(d.acwp * (s.patients / patientsPlanned));
      const pctExpected = d.bcws / budgetTotal;
      const adjustedCpi = pctExpected > 0 && siteActual > 0 ? (siteBudget * pctExpected) / siteActual : 1;
      const status = adjustedCpi >= 0.95 ? 'On Track' : adjustedCpi >= 0.85 ? 'Watch' : adjustedCpi >= 0.75 ? 'At Risk' : 'Critical';
      const projectedFinal = pctExpected > 0 ? siteActual / pctExpected : siteBudget;
      return { site: s.key, label: s.label, budget: siteBudget, actual: siteActual, cpi: parseFloat(adjustedCpi.toFixed(2)), status, projectedFinal: Math.round(projectedFinal) };
    });

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Section 1: EVM Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          {[
            { label: 'Cost Performance Index', value: d.cpi.toFixed(2), color: evmColor(d.cpi), tooltip: '1.0 = on budget. <1.0 = overspending' },
            { label: 'Schedule Performance Index', value: d.spi.toFixed(2), color: evmColor(d.spi), tooltip: '1.0 = on schedule. <1.0 = behind' },
            { label: 'Estimate at Completion', value: fmt(Math.round(d.eac)), color: d.eac <= budgetTotal ? '#48bb78' : '#f56565', tooltip: 'Projected total cost at current performance' },
            { label: 'Variance at Completion', value: fmt(Math.round(d.vac)), color: d.vac >= 0 ? '#48bb78' : '#f56565', tooltip: 'Budget minus EAC. Negative = overrun' },
          ].map((card) => (
            <div key={card.label} style={cardStyle} title={card.tooltip}>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{card.label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: card.color }}>{card.value}</div>
            </div>
          ))}
        </div>

        {/* Progress basis info line */}
        <div style={{ fontSize: 12, color: '#718096', marginTop: -8 }}>
          {d.trial_id} &middot; {curWeek > 0 ? `Week ${curWeek}` : 'Pre-trial baseline'} &middot; Progress basis: {d.progress_basis === 'enrollment' ? `Enrollment (${patientsEnrolled} / ${patientsPlanned} patients = ${(d.pct_complete * 100).toFixed(0)}%)` : `Schedule (${(d.pct_complete * 100).toFixed(1)}% of planned timeline)`}
        </div>

        {/* Section 2: Overrun Risk Banner */}
        <div style={{
          backgroundColor: sc.bg,
          border: `1px solid ${sc.border}`,
          borderRadius: 8,
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
            <span style={{ fontSize: 24, color: sc.color }}>{sc.icon}</span>
            <div>
              <span style={{
                display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                fontSize: 12, fontWeight: 700, color: sc.color, backgroundColor: 'rgba(0,0,0,0.3)', marginRight: 8,
              }}>{d.budget_status}</span>
              <span style={{ fontSize: 13, color: '#e2e8f0' }}>{d.recommendation}</span>
            </div>
          </div>
          <div style={{ textAlign: 'right', minWidth: 140 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: sc.color }}>{(d.overrun_probability * 100).toFixed(0)}%</div>
            <div style={{ fontSize: 11, color: '#a0aec0' }}>overrun risk</div>
            {d.weeks_to_breach > 0 && (
              <div style={{ fontSize: 11, color: sc.color, marginTop: 2 }}>{d.weeks_to_breach} weeks to breach</div>
            )}
          </div>
        </div>

        {/* Section 3: Spend Trajectory Chart */}
        <div style={cardStyle}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 12 }}>Spend Trajectory</div>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={trajectoryData} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2d3748" />
              <XAxis dataKey="week" tick={{ fill: '#a0aec0', fontSize: 10 }} label={{ value: 'Week', position: 'insideBottom', offset: -5, fill: '#a0aec0' }} />
              <YAxis tick={{ fill: '#a0aec0', fontSize: 10 }} tickFormatter={(v: number) => `$${(v / 1e6).toFixed(1)}M`} />
              <Tooltip contentStyle={{ backgroundColor: '#1a202c', border: '1px solid #2d3748', borderRadius: 6 }} formatter={(v: any) => [fmt(Number(v)), '']} labelFormatter={(l) => `Week ${l}`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine x={curWeek} stroke="#a0aec0" strokeDasharray="5 5" label={{ value: 'Now', fill: '#a0aec0', fontSize: 10 }} />
              <ReferenceLine y={budgetTotal} stroke="#f56565" strokeDasharray="5 5" label={{ value: 'Budget Cap', fill: '#f56565', fontSize: 10, position: 'right' }} />
              <Line type="monotone" dataKey="planned" stroke="#718096" strokeDasharray="5 5" dot={false} name="Planned (BCWS)" strokeWidth={2} />
              <Line type="monotone" dataKey="actual" stroke="#4299e1" dot={false} name="Actual (ACWP)" strokeWidth={2} />
              <Line type="monotone" dataKey="forecast" stroke="#f56565" strokeDasharray="5 5" dot={false} name="EAC Forecast" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Section 4: Weekly CPI Trend */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>Weekly CPI Trend</div>
            <span style={badgeStyle(d.cpi_trend === 'Improving' ? 'green' : d.cpi_trend === 'Degrading' ? 'red' : 'blue')}>{d.cpi_trend}</span>
          </div>
          <ResponsiveContainer width="100%" height={80}>
            <LineChart data={cpiHistory} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <ReferenceLine y={1.0} stroke="#48bb78" strokeDasharray="3 3" />
              <Line type="monotone" dataKey="cpi" stroke="#4299e1" strokeWidth={2} dot={{ r: 3, fill: '#4299e1' }} />
              <Tooltip contentStyle={{ backgroundColor: '#1a202c', border: '1px solid #2d3748', borderRadius: 6 }} formatter={(v: any) => [Number(v).toFixed(3), 'CPI']} labelFormatter={(l) => `Week ${l}`} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Section 5: Site Breakdown Table */}
        <div style={cardStyle}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 12 }}>Site Budget Performance</div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                {['Site', 'Budget', 'Actual', 'CPI', 'Status', 'Projected Final'].map((h) => (
                  <th key={h} style={{ ...thStyle, textAlign: h === 'Site' ? 'left' : 'right' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {siteData.map((s) => {
                // status color resolved inline via badgeStyle
                return (
                  <tr key={s.site}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{s.label}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(s.budget)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(s.actual)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: evmColor(s.cpi), fontWeight: 600 }}>{s.cpi.toFixed(2)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <span style={{ ...badgeStyle(s.status === 'On Track' ? 'green' : s.status === 'Watch' ? 'amber' : 'red') }}>{s.status}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: s.projectedFinal > s.budget ? '#f56565' : '#48bb78' }}>{fmt(s.projectedFinal)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Section 6: EVM Glossary */}
        <details style={{ ...cardStyle, cursor: 'pointer' }}>
          <summary style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>EVM Glossary</summary>
          <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {[
              { term: 'EVM', def: 'Earned Value Management — performance measurement technique' },
              { term: 'CPI', def: 'Cost Performance Index — BCWP/ACWP (1.0 = on budget)' },
              { term: 'SPI', def: 'Schedule Performance Index — BCWP/BCWS (1.0 = on schedule)' },
              { term: 'EAC', def: 'Estimate at Completion — projected total cost' },
              { term: 'ETC', def: 'Estimate to Complete — remaining cost to finish' },
              { term: 'VAC', def: 'Variance at Completion — budget minus EAC' },
              { term: 'BCWP', def: 'Budgeted Cost of Work Performed (Earned Value)' },
              { term: 'ACWP', def: 'Actual Cost of Work Performed' },
              { term: 'BCWS', def: 'Budgeted Cost of Work Scheduled (Planned Value)' },
            ].map((g) => (
              <div key={g.term} style={{ fontSize: 11 }}>
                <span style={{ color: '#63b3ed', fontWeight: 600 }}>{g.term}</span>
                <span style={{ color: '#a0aec0' }}> — {g.def}</span>
              </div>
            ))}
          </div>
        </details>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Tab: Calculate Budget (SOA-driven)
  // ---------------------------------------------------------------------------

  const getSOAGridForProtocol = (): Record<string, Record<string, boolean>> | null => {
    // Builtin protocols
    if (BUILTIN_SOA_GRIDS[selectedProtocolId]) return BUILTIN_SOA_GRIDS[selectedProtocolId];
    // Imported protocols — check localStorage parsed data
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const entries = JSON.parse(raw);
        for (const entry of entries) {
          if (entry.id === selectedProtocolId && entry.config?.procedures) {
            // Reconstruct soa_grid from ProtocolConfig procedures
            const grid: Record<string, Record<string, boolean>> = {};
            for (const proc of entry.config.procedures) {
              grid[proc.name] = {};
              for (const v of entry.config.visits) {
                grid[proc.name][v] = proc.visits?.includes(v) ?? false;
              }
            }
            return grid;
          }
        }
      }
    } catch { /* ignore */ }
    return null;
  };

  const handleCalcBudget = async () => {
    const soaGrid = getSOAGridForProtocol();
    if (!soaGrid) {
      setCalcBudgetError('No SOA grid available for this protocol. Import a protocol PDF first.');
      return;
    }
    setCalcBudgetLoading(true);
    setCalcBudgetError('');
    try {
      const API_BASE = process.env.REACT_APP_API_URL || '';
      // Merge unmapped manual rates into overrides
      const allOverrides = { ...calcRateOverrides, ...calcUnmappedRates };
      const res = await fetch(`${API_BASE}/api/v1/budget/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          protocol_id: selectedProtocolId,
          soa_grid: soaGrid,
          n_patients: calcNPatients,
          n_sites: calcNSites,
          trial_duration_months: calcDurationMonths,
          overhead_pct: calcOverheadPct / 100,
          site_name: protocol.sites[0]?.label ?? 'UCLA',
          cpt_rate_overrides: allOverrides,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `API error ${res.status}`);
      }
      const data = await res.json();
      setCalcBudgetResult(data);

      // Auto-feed to M19 forecast
      try {
        const fRes = await fetch(`${API_BASE}/api/v1/forecasting/from-protocol`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            protocol_id: selectedProtocolId,
            total_trial_budget: data.total_trial_budget,
            per_patient_cost: data.per_patient_cost,
            n_patients: data.n_patients,
            visit_costs: {},
            total_weeks: Math.round(calcDurationMonths * 4.33),
            enrolment_rate_per_week: 1.0,
            actual_spend_by_week: [],
            current_week: 0,
            patients_enrolled: 0,
          }),
        });
        if (fRes.ok) setForecastData(await fRes.json());
      } catch { /* forecast optional */ }
    } catch (err: any) {
      setCalcBudgetError(err.message || 'Budget calculation failed.');
    } finally {
      setCalcBudgetLoading(false);
    }
  };

  const renderCalcBudget = () => {
    const soaGrid = getSOAGridForProtocol();
    const hasSoa = !!soaGrid;
    const r = calcBudgetResult;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Section 1: Budget Inputs */}
        <div style={cardStyle}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 16 }}>Budget Parameters</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Protocol</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0' }}>{protocol.trialName}</div>
              {!hasSoa && <div style={{ fontSize: 11, color: '#ed8936', marginTop: 4 }}>No SOA grid — import a PDF first</div>}
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Patients</div>
              <input type="number" value={calcNPatients} onChange={(e) => setCalcNPatients(parseInt(e.target.value) || 1)}
                style={{ width: '100%', backgroundColor: '#2d3748', border: '1px solid #4a5568', borderRadius: 4, color: '#e2e8f0', fontSize: 14, fontWeight: 600, padding: '8px 10px' }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Sites</div>
              <input type="number" value={calcNSites} onChange={(e) => setCalcNSites(parseInt(e.target.value) || 1)}
                style={{ width: '100%', backgroundColor: '#2d3748', border: '1px solid #4a5568', borderRadius: 4, color: '#e2e8f0', fontSize: 14, fontWeight: 600, padding: '8px 10px' }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Duration (months)</div>
              <input type="number" value={calcDurationMonths} onChange={(e) => setCalcDurationMonths(parseInt(e.target.value) || 1)}
                style={{ width: '100%', backgroundColor: '#2d3748', border: '1px solid #4a5568', borderRadius: 4, color: '#e2e8f0', fontSize: 14, fontWeight: 600, padding: '8px 10px' }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Overhead %</div>
              <input type="number" value={calcOverheadPct} onChange={(e) => setCalcOverheadPct(parseFloat(e.target.value) || 0)}
                style={{ width: '100%', backgroundColor: '#2d3748', border: '1px solid #4a5568', borderRadius: 4, color: '#e2e8f0', fontSize: 14, fontWeight: 600, padding: '8px 10px' }} />
            </div>
          </div>
        </div>

        {/* Section 2: Calculate Button */}
        <button
          onClick={handleCalcBudget}
          disabled={!hasSoa || calcBudgetLoading}
          style={{
            width: '100%', padding: '16px 24px', borderRadius: 8, fontSize: 16, fontWeight: 700,
            border: 'none', cursor: hasSoa ? 'pointer' : 'not-allowed',
            backgroundColor: hasSoa ? '#2b6cb0' : '#4a5568',
            color: '#fff', transition: 'all 0.2s',
          }}
        >
          {calcBudgetLoading ? 'Mapping procedures to CPT codes...' : r ? 'Recalculate Budget' : 'Calculate Budget'}
        </button>

        {calcBudgetError && (
          <div style={{ ...cardStyle, border: '1px solid #742a2a' }}>
            <div style={{ color: '#f56565', fontSize: 13, fontWeight: 600 }}>{calcBudgetError}</div>
          </div>
        )}

        {/* Section 3: Results */}
        {r && (
          <>
            {/* ROW 1 — Summary cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              <div style={cardStyle}>
                <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5 }}>Per-Patient</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#48bb78' }}>{fmt(Math.round(r.per_patient_cost))}</div>
              </div>
              <div style={cardStyle}>
                <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Budget</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#4299e1' }}>{fmt(Math.round(r.total_trial_budget))}</div>
              </div>
              <div style={cardStyle}>
                <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5 }}>Mapping Confidence</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: r.mapping_confidence >= 0.9 ? '#48bb78' : r.mapping_confidence >= 0.7 ? '#ecc94b' : '#f56565' }}>
                  {(r.mapping_confidence * 100).toFixed(0)}%
                </div>
              </div>
              <div style={cardStyle}>
                <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 0.5 }}>Unmapped</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: r.unmapped_procedures.length > 0 ? '#ed8936' : '#48bb78' }}>
                  {r.unmapped_procedures.length} procedure{r.unmapped_procedures.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>

            {/* ROW 2 — Cost breakdown bar */}
            <div style={cardStyle}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 12 }}>Cost Breakdown</div>
              <div style={{ display: 'flex', height: 32, borderRadius: 6, overflow: 'hidden', marginBottom: 12 }}>
                {[
                  { key: 'clinical', label: 'Clinical', color: '#4299e1' },
                  { key: 'setup', label: 'Setup', color: '#48bb78' },
                  { key: 'monitoring', label: 'Monitoring', color: '#ecc94b' },
                  { key: 'data_management', label: 'DM', color: '#ed8936' },
                  { key: 'overhead', label: 'Overhead', color: '#f56565' },
                ].map((seg) => {
                  const pct = r.cost_breakdown_pct[seg.key] || 0;
                  return pct > 0 ? (
                    <div key={seg.key} style={{ width: `${pct}%`, backgroundColor: seg.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', minWidth: pct > 5 ? 0 : 0 }}>
                      {pct >= 5 ? `${seg.label} ${pct}%` : ''}
                    </div>
                  ) : null;
                })}
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {[
                  { label: 'Clinical', value: r.clinical_direct_cost, pct: r.cost_breakdown_pct.clinical, color: '#4299e1' },
                  { label: 'Setup', value: r.site_setup_cost, pct: r.cost_breakdown_pct.setup, color: '#48bb78' },
                  { label: 'Monitoring', value: r.monitoring_cost, pct: r.cost_breakdown_pct.monitoring, color: '#ecc94b' },
                  { label: 'Data Mgmt', value: r.data_management_cost, pct: r.cost_breakdown_pct.data_management, color: '#ed8936' },
                  { label: 'Overhead', value: r.overhead, pct: r.cost_breakdown_pct.overhead, color: '#f56565' },
                ].map((item) => (
                  <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: item.color }} />
                    <span style={{ fontSize: 12, color: '#a0aec0' }}>{item.label}</span>
                    <span style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 600 }}>{fmt(Math.round(item.value))} ({item.pct}%)</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ROW 3 — Top cost drivers */}
            <div style={{ ...cardStyle, overflowX: 'auto' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 12 }}>Top Cost Drivers</div>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Procedure</th>
                    <th style={thStyle}>CPT</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Rate</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Occurrences</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {r.top_cost_drivers.map((d: any) => {
                    const pc = r.procedure_costs[d.procedure];
                    return (
                      <tr key={d.procedure}>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>
                          {d.procedure}
                          {pc?.matched_to && <span style={{ fontSize: 10, color: '#718096', marginLeft: 6 }}>(matched: {pc.matched_to})</span>}
                        </td>
                        <td style={tdStyle}><span style={badgeStyle('blue')}>{d.cpt}</span></td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(pc?.rate ?? 0)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{pc?.occurrences ?? 0}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#48bb78' }}>{fmt(Math.round(d.total))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ROW 4 — Per-visit breakdown */}
            <div style={{ ...cardStyle, overflowX: 'auto' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 12 }}>Per-Visit Breakdown</div>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Visit</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Procedures</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Cost per Patient</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Total (all patients)</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(r.visit_costs).map(([visit, vc]: [string, any]) => (
                    <tr key={visit}>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{visit}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{vc.procedures}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{r.n_patients > 0 ? fmt(Math.round(vc.cost / r.n_patients)) : '$0'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{fmt(Math.round(vc.cost))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ROW 5 — Unmapped procedures */}
            {r.unmapped_procedures.length > 0 && (
              <div style={{ ...cardStyle, border: '1px solid #ed8936', backgroundColor: '#744210' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#ecc94b', marginBottom: 8 }}>
                  {r.unmapped_procedures.length} procedure{r.unmapped_procedures.length !== 1 ? 's' : ''} could not be mapped to a CPT code:
                </div>
                {r.unmapped_procedures.map((proc: string) => (
                  <div key={proc} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: '#ecc94b', fontWeight: 600, minWidth: 200 }}>{proc}</span>
                    <span style={{ fontSize: 11, color: '#a0aec0' }}>Assign rate: $</span>
                    <input
                      type="number"
                      placeholder="0"
                      value={calcUnmappedRates[proc] || ''}
                      onChange={(e) => setCalcUnmappedRates((prev) => ({ ...prev, [proc]: parseFloat(e.target.value) || 0 }))}
                      style={{ width: 100, backgroundColor: '#2d3748', border: '1px solid #4a5568', borderRadius: 4, color: '#e2e8f0', fontSize: 13, padding: '4px 8px' }}
                    />
                  </div>
                ))}
                <button
                  onClick={handleCalcBudget}
                  style={{ marginTop: 8, padding: '6px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', backgroundColor: '#ed8936', color: '#fff' }}
                >
                  Recalculate with manual rates
                </button>
              </div>
            )}

            {/* ROW 6 — CPT Rate Overrides (collapsible) */}
            <details open={calcShowOverrides} onToggle={(e) => setCalcShowOverrides((e.target as HTMLDetailsElement).open)}>
              <summary style={{ ...cardStyle, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>
                Override Default Rates ({Object.keys(calcRateOverrides).length} overrides)
              </summary>
              <div style={{ ...cardStyle, marginTop: 4, maxHeight: 400, overflowY: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Procedure</th>
                      <th style={thStyle}>CPT</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Default Rate</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Your Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(r.procedure_costs).map(([proc, pc]: [string, any]) => (
                      <tr key={proc}>
                        <td style={{ ...tdStyle, fontWeight: 600, fontSize: 12 }}>{proc}</td>
                        <td style={tdStyle}><span style={badgeStyle('blue')}>{pc.cpt}</span></td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(pc.rate)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', cursor: 'pointer' }}>
                          {calcEditingRate === proc ? (
                            <input
                              type="number"
                              defaultValue={calcRateOverrides[proc] ?? pc.rate}
                              autoFocus
                              onBlur={(e) => {
                                const v = parseFloat(e.target.value);
                                if (!isNaN(v) && v >= 0) setCalcRateOverrides((prev) => ({ ...prev, [proc]: v }));
                                setCalcEditingRate(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const v = parseFloat((e.target as HTMLInputElement).value);
                                  if (!isNaN(v) && v >= 0) setCalcRateOverrides((prev) => ({ ...prev, [proc]: v }));
                                  setCalcEditingRate(null);
                                }
                              }}
                              style={{ width: 80, backgroundColor: '#2d3748', border: '1px solid #4a5568', borderRadius: 4, color: '#e2e8f0', fontSize: 13, padding: '2px 6px', textAlign: 'right' }}
                            />
                          ) : (
                            <span onClick={() => setCalcEditingRate(proc)} style={{ color: calcRateOverrides[proc] !== undefined ? '#ecc94b' : '#e2e8f0' }}>
                              {fmt(calcRateOverrides[proc] ?? pc.rate)}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {Object.keys(calcRateOverrides).length > 0 && (
                  <button
                    onClick={handleCalcBudget}
                    style={{ marginTop: 12, padding: '8px 20px', borderRadius: 6, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', backgroundColor: '#2b6cb0', color: '#fff' }}
                  >
                    Recalculate with overrides
                  </button>
                )}
              </div>
            </details>

            {/* Forecast updated toast */}
            {forecastData && (
              <div style={{ backgroundColor: '#22543d', border: '1px solid #48bb78', borderRadius: 8, padding: '10px 20px' }}>
                <span style={{ color: '#48bb78', fontSize: 13, fontWeight: 600 }}>Forecast updated from budget calculation</span>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Main Render
  // ---------------------------------------------------------------------------
  return (
    <div style={{ padding: 32 }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#e2e8f0', margin: 0 }}>Clinical Trial Budget Calculator</h1>
          <ComplianceBadges badges={['ICH E6(R3)', '21 CFR 312', 'ALCOA']} />
        </div>
        <ViewModeToggle />
      </div>

      {/* Protocol Selector — always visible */}
      <div style={{
        backgroundColor: '#1a202c',
        border: '1px solid #2d3748',
        borderRadius: 8,
        padding: '16px 20px',
        marginBottom: 16,
      }}>
        <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600, marginBottom: 8 }}>
          Active Protocol
        </div>
        <select
          value={selectedProtocolId}
          onChange={(e) => handleProtocolChange(e.target.value)}
          style={{
            width: '100%',
            maxWidth: 520,
            backgroundColor: '#e2e8f0',
            border: '2px solid #4299e1',
            borderRadius: 6,
            color: '#1a202c',
            fontSize: 15,
            fontWeight: 700,
            padding: '10px 14px',
            cursor: 'pointer',
            appearance: 'auto' as any,
          }}
        >
          {protocolList.map((p) => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>

        {/* Stat pills */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          {[
            { label: `Phase ${protocol.phase}`, color: '#4299e1', bg: '#1a365d' },
            { label: `${protocol.totalPatients.toLocaleString()} patients`, color: '#48bb78', bg: '#22543d' },
            { label: `${protocol.visits.length} visits`, color: '#ecc94b', bg: '#744210' },
            { label: `${fmt(Math.round(quickPerPatient))}/patient`, color: '#f56565', bg: '#742a2a' },
            { label: indicationMap[protocol.id] || protocol.phase, color: '#b794f4', bg: '#44337a' },
          ].map((pill) => (
            <span
              key={pill.label}
              style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600,
                color: pill.color,
                backgroundColor: pill.bg,
              }}
            >
              {pill.label}
            </span>
          ))}
        </div>
      </div>

      {/* Import success banner */}
      {importSuccess && (
        <div style={{
          backgroundColor: '#22543d',
          border: '1px solid #48bb78',
          borderRadius: 8,
          padding: '12px 20px',
          marginBottom: 12,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{ color: '#48bb78', fontSize: 14, fontWeight: 600 }}>
            {'\u2713'} Protocol "{importSuccess}" imported and added to your protocol list
          </span>
          <button
            onClick={() => setImportSuccess(null)}
            style={{ background: 'none', border: 'none', color: '#48bb78', cursor: 'pointer', fontSize: 16 }}
          >
            {'\u2715'}
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
        {tabs.map((t) => (
          <button key={t.key} style={tabBtnStyle(activeTab === t.key)} onClick={() => setActiveTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'calcbudget' && renderCalcBudget()}
      {activeTab === 'import' && renderImport()}
      {activeTab === 'soa' && renderSOA()}
      {activeTab === 'cpt' && renderCPT()}
      {activeTab === 'perpatient' && renderPerPatient()}
      {activeTab === 'summary' && renderSummary()}
      {activeTab === 'compare' && renderCompare()}
      {activeTab === 'forecast' && renderForecast()}

      {/* Glossary */}
      <Glossary entries={GLOSSARY_ENTRIES} />
    </div>
  );
}
